/**
 * The Link Accounting prototype dataset: two fictional companies with their
 * documents, payments, bank lines and postings. Prototype only — nothing here
 * comes from or goes to the backend, and every name and number is invented.
 *
 * Money, rates and quantities are decimal strings; JOD amounts carry three
 * decimals. Every sum and comparison goes through @linkhr/accounting-domain,
 * never `Number()`. Dates are ISO calendar dates; names are `{ ar, en }`.
 * `fixtures.test.ts` proves the books balance and agree with the documents.
 */
import { addMinor, cmpMinor, subMinor, sumMinor } from "@linkhr/accounting-domain";
import { dateToIso, isoToDate } from "@linkhr/i18n/format";
import aladwan1192 from "./docs/aladwan-1192.svg";
import alamal5521 from "./docs/alamal-5521.svg";
import alnoorOct from "./docs/alnoor-r-2026-10.svg";
import alsharq3310 from "./docs/alsharq-as-3310.svg";
import alsharq3402 from "./docs/alsharq-as-3402.svg";
import alsharq4512 from "./docs/alsharq-4512.svg";
import blurryPhoto from "./docs/blurry-photo.svg";
import globalOffice5480 from "./docs/globaloffice-5480.svg";
import globalOffice5529 from "./docs/globaloffice-5529.svg";
import globalOffice5541 from "./docs/globaloffice-5541.svg";
import orange5512 from "./docs/orange-64-5512.svg";
import orange5571 from "./docs/orange-64-5571.svg";
import orange5590 from "./docs/orange-64-5590.svg";
import petra7715 from "./docs/petra-7715.svg";
import petra7728 from "./docs/petra-7728.svg";
import petra7731 from "./docs/petra-7731.svg";
import shenzhen2410 from "./docs/shenzhen-sb-2410.svg";

/** The prototype's clock is fixed: Friday 9 October 2026, noon in Amman. */
export const TODAY = "2026-10-09";
export const NOW = "2026-10-09T12:00:00+03:00";
/** JOD has three decimals. */
export const SCALE = 3;
const ZERO = "0.000";

export type Decimal = string;
export interface Bilingual {
  ar: string;
  en: string;
}

// ---------------------------------------------------------------------------
// Vocabulary. The names belong to the prototype; the contract brings the final ones.
// ---------------------------------------------------------------------------

export const MODES = ["owner", "accountant"] as const;
export type Mode = (typeof MODES)[number];

export const PRODUCT_STATES = ["ACTIVE", "TRIAL", "READ_ONLY", "LOCKED"] as const;
export type ProductState = (typeof PRODUCT_STATES)[number];

export type UserId = "samer" | "layla" | "rana";
export const PERIOD_STATES = ["OPEN", "SOFT_CLOSED", "HARD_CLOSED"] as const;
export type PeriodState = (typeof PERIOD_STATES)[number];
export const DOCUMENT_STATES = [
  "DRAFT",
  "IN_REVIEW",
  "CHANGES_REQUESTED",
  "APPROVED",
  "POSTED",
  "VOID_BEFORE_POSTING",
] as const;
export type DocumentState = (typeof DOCUMENT_STATES)[number];
/** Derived from what payments applied; OVERDUE also needs the due date. */
export const SETTLEMENT_STATES = ["UNPAID", "PARTLY_SETTLED", "SETTLED", "OVERDUE"] as const;
export type SettlementState = (typeof SETTLEMENT_STATES)[number];
export const EVIDENCE_STATES = ["EINVOICE_PRESENT", "EINVOICE_MISSING", "NOT_APPLICABLE"] as const;
export type EvidenceState = (typeof EVIDENCE_STATES)[number];
/** What was done with an e-invoice QR. Never "verified". */
export const QR_STATES = ["DECODED", "STRUCTURALLY_VALID", "CROSS_CHECKED"] as const;
export type QrState = (typeof QR_STATES)[number];
/** Where a sales invoice stands with JoFotara. UNKNOWN_OUTCOME is never a rejection. */
export const FISCAL_STATES = [
  "REQUIRED",
  "PREPARED",
  "SEALED",
  "QUEUED",
  "SUBMITTED",
  "ACCEPTED",
  "ACCEPTED_WITH_WARNINGS",
  "REJECTED",
  "RETRYING",
  "UNKNOWN_OUTCOME",
  "NOT_ACTIVATED",
] as const;
export type FiscalState = (typeof FISCAL_STATES)[number];
/** The owner's five words for the fiscal states. */
export const FISCAL_GROUPS = ["NOT_SENT", "SENDING", "ACCEPTED", "NEEDS_FIX", "WAITING"] as const;
export type FiscalGroup = (typeof FISCAL_GROUPS)[number];
export const fiscalGroup = (state: FiscalState): FiscalGroup =>
  state === "ACCEPTED" || state === "ACCEPTED_WITH_WARNINGS"
    ? "ACCEPTED"
    : state === "REJECTED"
      ? "NEEDS_FIX"
      : state === "UNKNOWN_OUTCOME"
        ? "WAITING"
        : state === "REQUIRED" || state === "NOT_ACTIVATED"
          ? "NOT_SENT"
          : "SENDING";
/** Only an accepted invoice is official: print, email, share and the public link wait for it. */
export const fiscalOfficial = (state: FiscalState): boolean => fiscalGroup(state) === "ACCEPTED";
/** The steps an issued invoice runs through on its way to JoFotara. */
export const FISCAL_STEPS: readonly FiscalState[] = ["PREPARED", "SEALED", "QUEUED", "SUBMITTED"];
/** What a rejection needs fixed; the fix changes buyer or fiscal details only. */
export const FISCAL_FIXES = ["BUYER_ID"] as const;
export type FiscalFix = (typeof FISCAL_FIXES)[number];
/** APPROVED: a first person approved it and it waits for release (two people for payments). */
export const PAYMENT_STATES = [
  "DRAFT",
  "APPROVED",
  "RELEASED",
  "SETTLED",
  "FAILED",
  "UNKNOWN",
] as const;
export type PaymentState = (typeof PAYMENT_STATES)[number];
export const INTAKE_STATES = [
  "PROCESSING",
  "NEEDS_REVIEW",
  "READY",
  "WAITING_ON_OTHERS",
  "QUARANTINED",
  "SCAN_FAILED",
  "UNREADABLE",
  "DUPLICATE_BLOCKED",
  "POSSIBLE_DUPLICATE",
  "POSTED",
  "REJECTED",
] as const;
export type IntakeState = (typeof INTAKE_STATES)[number];
export const RECON_LINE_STATES = [
  "SUGGESTED_EXACT",
  "NEEDS_REVIEW",
  "NO_SUGGESTION",
  "DEFERRED",
  "MATCHED",
  "PENDING_AT_BANK",
] as const;
export type ReconLineState = (typeof RECON_LINE_STATES)[number];
/** Why a cheque came back (fixture reasons; the real list is the central bank's). */
export const RETURN_REASONS = [
  "INSUFFICIENT_FUNDS",
  "SIGNATURE_MISMATCH",
  "STOPPED_BY_DRAWER",
  "ACCOUNT_CLOSED",
] as const;
export type ReturnReason = (typeof RETURN_REASONS)[number];
export const CHEQUE_STATES = [
  "IN_SAFE",
  "UNDER_COLLECTION",
  "CLEARED",
  "RETURNED",
  "ISSUED",
  "PRESENTED",
  "CANCELLED",
] as const;
export type ChequeState = (typeof CHEQUE_STATES)[number];
export const JOURNAL_STATES = ["DRAFT", "POSTED", "REVERSED"] as const;
export type JournalState = (typeof JOURNAL_STATES)[number];
/** The books a manual entry is kept in. */
export const JOURNAL_BOOKS = ["GENERAL", "ADJUSTMENTS"] as const;
export type JournalBook = (typeof JOURNAL_BOOKS)[number];
/** Accounts that need a customer or supplier on every line. */
export const CONTROL_ACCOUNTS: readonly string[] = ["1120", "2101"];
export const REASON_CODES = [
  "READY",
  "LOW_CONFIDENCE_FIELDS",
  "NEW_SUPPLIER",
  "HIDDEN_INSTRUCTIONS",
  "UNREADABLE",
  "EXACT_DUPLICATE",
  "POSSIBLE_DUPLICATE",
  "IBAN_CHANGED",
  "NO_JOFOTARA_INVOICE",
  "QR_TOTAL_MISMATCH",
  "EINVOICE_REJECTED",
  "EINVOICE_OUTCOME_UNKNOWN",
  "PAYMENT_AWAITING_RELEASE",
  "UNRECONCILED_LINES",
  "PERIOD_LOCKED",
  "BACKDATED_ENTRY",
  "OWNER_EASY_ENTRY",
  "QUESTION_FOR_OWNER",
] as const;
export type ReasonCode = (typeof REASON_CODES)[number];
export const REJECT_REASONS = ["DUPLICATE", "NOT_OURS", "WRONG_AMOUNT", "OTHER"] as const;
export type RejectReason = (typeof REJECT_REASONS)[number];
/** The owner's money events with no document; the list is data, so more types are cheap later. */
export const EASY_ENTRY_TYPES = ["OWNER_IN", "OWNER_OUT", "TRANSFER"] as const;
export type EasyEntryType = (typeof EASY_ENTRY_TYPES)[number];
export const PAYMENT_METHODS = ["BANK_TRANSFER", "CLIQ", "CHEQUE", "CASH"] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];
/** What happens to the rest of a bill a payment does not cover. */
export const SHORT_CHOICES = ["KEEP_OPEN", "WRITE_OFF"] as const;
export type ShortChoice = (typeof SHORT_CHOICES)[number];
/** The events an activity log shows. */
export const ACTIVITY_KINDS = [
  "AI_DRAFTED",
  "CREATED",
  "EDITED",
  "SUBMITTED",
  "APPROVED",
  "APPROVAL_CLEARED",
  "CHANGES_REQUESTED",
  "POSTED",
  "PAID",
  "ATTACHED",
  "PREPARED",
  "RELEASED",
  "SETTLED",
  "WRITTEN_OFF",
  "PERIOD_EXCEPTION",
  "LOCK_EXCEPTION",
  "CREDITED",
] as const;
export type ActivityKind = (typeof ACTIVITY_KINDS)[number];
export const BUYER_ID_TYPES = ["NIN", "PN", "TN"] as const;
export type BuyerIdType = (typeof BUYER_ID_TYPES)[number];
/** S = standard 16% · Z = zero-rated · O = outside the tax. */
export const TAX_CODES = ["S", "Z", "O"] as const;
export type TaxCode = (typeof TAX_CODES)[number];
/** How sure the AI is about a field it read. */
export const BANDS = ["CLEAR", "CHECK", "NOT_SURE", "NOT_FOUND"] as const;
export type Band = (typeof BANDS)[number];
/** Where a field's value came from. Who changed it last is a separate fact. */
export const ORIGINS = ["READ", "INFERRED", "CALCULATED", "CORRECTED"] as const;
export type Origin = (typeof ORIGINS)[number];

// ---------------------------------------------------------------------------
// Shared records
// ---------------------------------------------------------------------------

export type CompanyId = "nada-trading" | "nada-distribution";
export const DEFAULT_COMPANY: CompanyId = "nada-trading";

export interface Company {
  id: CompanyId;
  name: Bilingual;
  city: Bilingual;
  taxNumber: string;
  currency: "JOD";
  branches: readonly { id: string; name: Bilingual }[];
  trialEndsOn: string;
  /** Who approves the owner's easy entries; with none, the owner confirms them. */
  accountant: UserId | null;
}

export const COMPANIES: readonly Company[] = [
  {
    id: "nada-trading",
    name: { ar: "شركة الندى للتجارة العامة ذ.م.م", en: "Al-Nada General Trading LLC" },
    city: { ar: "عمّان", en: "Amman" },
    taxNumber: "10234567",
    currency: "JOD",
    branches: [
      { id: "amman-main", name: { ar: "عمّان – الرئيسي", en: "Amman – Main" } },
      { id: "zarqa", name: { ar: "الزرقاء", en: "Zarqa" } },
    ],
    trialEndsOn: "2026-11-08",
    accountant: "layla",
  },
  {
    id: "nada-distribution",
    name: { ar: "شركة الندى للتوزيع", en: "Al-Nada Distribution" },
    city: { ar: "عمّان", en: "Amman" },
    taxNumber: "10234575",
    currency: "JOD",
    branches: [{ id: "amman-main", name: { ar: "عمّان – الرئيسي", en: "Amman – Main" } }],
    trialEndsOn: "2026-11-08",
    accountant: null,
  },
];

export const isCompanyId = (id: string): id is CompanyId =>
  COMPANIES.some((company) => company.id === id);

export const companyById = (id: CompanyId): Company =>
  COMPANIES.find((company) => company.id === id) ?? (COMPANIES[0] as Company);

/** The prototype's slice of the permission codes a company grants a person. */
export type Grant =
  | "bill:approve"
  | "bill:prepare"
  | "payment:release"
  | "sales:approve"
  | "journal:post"
  | "bank:import"
  | "bank:reconcile"
  | "audit:read";
/** Holding any of these opens the books in Accountant view. */
const ACCOUNTANT_GRANTS: readonly Grant[] = [
  "journal:post",
  "bank:import",
  "bank:reconcile",
  "audit:read",
];

export interface User {
  id: UserId;
  name: Bilingual;
  role: "OWNER" | "ACCOUNTANT" | "CLERK";
  email: string;
  grants: readonly Grant[];
}

export const USERS: Readonly<Record<UserId, User>> = {
  samer: {
    id: "samer",
    name: { ar: "سامر الخطيب", en: "Samer Al-Khatib" },
    role: "OWNER",
    email: "samer@nada-trading.example",
    grants: ["bill:approve", "payment:release", "sales:approve"],
  },
  layla: {
    id: "layla",
    name: { ar: "ليلى حداد", en: "Layla Haddad" },
    role: "ACCOUNTANT",
    email: "layla@haddad-accounting.example",
    grants: ["journal:post", "bank:import", "bank:reconcile", "bill:approve"],
  },
  rana: {
    id: "rana",
    name: { ar: "رنا عودة", en: "Rana Odeh" },
    role: "CLERK",
    email: "rana@nada-trading.example",
    grants: ["bill:prepare"],
  },
};

/** The fixture user each mode signs in as: the owner, or the outside accountant. */
export const userForMode = (mode: Mode): User => (mode === "owner" ? USERS.samer : USERS.layla);

/** The view a person's grant opens in: Accountant view for bookkeeping grants, else Business view. */
export const defaultMode = (user: User): Mode =>
  user.grants.some((grant) => ACCOUNTANT_GRANTS.includes(grant)) ? "accountant" : "owner";

export interface Period {
  id: string;
  from: string;
  to: string;
  state: PeriodState;
  closePackOn?: string;
}

export const PERIODS: readonly Period[] = [
  { id: "2026-08", from: "2026-08-01", to: "2026-08-31", state: "HARD_CLOSED" },
  {
    id: "2026-09",
    from: "2026-09-01",
    to: "2026-09-30",
    state: "SOFT_CLOSED",
    closePackOn: "2026-10-05",
  },
  { id: "2026-10", from: "2026-10-01", to: "2026-10-31", state: "OPEN" },
];

export interface Account {
  code: string;
  name: Bilingual;
  /** Group accounts have no parent and take no postings. */
  parent?: string;
  /** The owner's word for a leaf, shown without the code. */
  ownerCategory?: Bilingual;
}

const group = (code: string, en: string, ar: string): Account => ({ code, name: { en, ar } });
const leaf = (code: string, name: [string, string], owner: [string, string]): Account => ({
  code,
  name: { en: name[0], ar: name[1] },
  parent: code.slice(0, 1),
  ownerCategory: { en: owner[0], ar: owner[1] },
});

/** A Jordanian chart: classes 1–5 as groups, leaves under them. */
export const ACCOUNTS: readonly Account[] = [
  group("1", "Assets", "الأصول"),
  leaf("1101", ["Cash box", "الصندوق"], ["Cash", "النقد"]),
  leaf("1102", ["Arab Bank – current", "البنك العربي – جاري"], ["Arab Bank", "البنك العربي"]),
  leaf("1103", ["Housing Bank", "بنك الإسكان"], ["Housing Bank", "بنك الإسكان"]),
  leaf("1120", ["Accounts receivable", "الذمم المدينة"], ["Owed to you", "مستحق لك"]),
  leaf(
    "1130",
    ["Cheques under collection", "شيكات برسم التحصيل"],
    ["Cheques at the bank", "شيكات في البنك"],
  ),
  leaf(
    "1140",
    ["Input GST", "ضريبة المبيعات على المشتريات"],
    ["Tax you can claim back", "ضريبة يمكن استردادها"],
  ),
  group("2", "Liabilities", "الالتزامات"),
  leaf("2101", ["Accounts payable", "الذمم الدائنة"], ["You owe", "مستحق عليك"]),
  leaf("2110", ["Cheques issued", "شيكات صادرة"], ["Cheques you wrote", "شيكات أصدرتها"]),
  leaf(
    "2120",
    ["Output GST", "ضريبة المبيعات على المبيعات"],
    ["Sales tax to pay", "ضريبة مبيعات مستحقة"],
  ),
  leaf(
    "2130",
    ["WHT payable", "ضريبة مقتطعة مستحقة"],
    ["Tax kept for the tax office", "ضريبة محتجزة لدائرة الضريبة"],
  ),
  leaf(
    "2140",
    ["Accrued expenses", "مصاريف مستحقة"],
    ["Costs not billed yet", "تكاليف لم تصل فواتيرها"],
  ),
  group("3", "Equity", "حقوق الملكية"),
  leaf("3101", ["Capital", "رأس المال"], ["Money you started with", "رأس المال"]),
  leaf(
    "3102",
    ["Owner's current account", "جاري المالك"],
    ["Money you put in or took out", "إيداعات المالك ومسحوباته"],
  ),
  group("4", "Revenue", "الإيرادات"),
  leaf("4101", ["Sales", "المبيعات"], ["Sales", "المبيعات"]),
  leaf("4190", ["Discounts received", "خصم مكتسب"], ["Discount received", "خصم حصلت عليه"]),
  group("5", "Expenses", "المصاريف"),
  leaf("5101", ["Cost of goods sold", "تكلفة البضاعة المباعة"], ["Cost of goods", "تكلفة البضاعة"]),
  leaf("5120", ["Rent", "الإيجار"], ["Rent", "الإيجار"]),
  leaf("5130", ["Telephone", "الهاتف والإنترنت"], ["Phone and internet", "الهاتف والإنترنت"]),
  leaf("5140", ["Professional fees", "أتعاب مهنية"], ["Legal and audit", "أتعاب قانونية وتدقيق"]),
  leaf("5150", ["Packaging", "مواد التغليف"], ["Packaging", "التغليف"]),
  leaf("5160", ["Bank charges", "عمولات بنكية"], ["Bank fees", "رسوم البنك"]),
  leaf("5170", ["FX differences", "فروقات العملة"], ["Currency differences", "فروقات العملة"]),
  leaf("5180", ["Electricity", "الكهرباء"], ["Electricity", "الكهرباء"]),
  leaf("5190", ["Vehicle fuel", "وقود المركبات"], ["Fuel", "الوقود"]),
];

// ---------------------------------------------------------------------------
// Per-company records
// ---------------------------------------------------------------------------

export interface BankAccount {
  id: string;
  /** The ledger account it posts to. */
  account: string;
  name: Bilingual;
  reconciledTo: string | null;
}

export interface Supplier {
  id: string;
  name: Bilingual;
  currency: "JOD" | "USD";
  email?: string;
  /** Bank details on file. Documents never change them. */
  bankAccount: { bank: Bilingual; number: string; verifiedOn: string } | null;
}

export interface Customer {
  id: string;
  name: Bilingual;
  /** Needed on credit invoices; Diyafa's is missing on purpose (S21). */
  taxNumber: string | null;
}

export interface DocLine {
  description: Bilingual;
  account: string;
  quantity: Decimal;
  unitPrice: Decimal;
  amount: Decimal;
  taxCode: TaxCode;
  tax: Decimal;
  /** A discount on this line; JoFotara takes discounts per line only. */
  discount?: Decimal;
  /** Dimensions; a line without them takes the document's branch and the default cost centre. */
  branch?: string;
  costCentre?: string;
}

/** Cost centres, the second dimension the accountant sees per line. */
export const COST_CENTRES: readonly { id: string; name: Bilingual }[] = [
  { id: "operations", name: { ar: "العمليات", en: "Operations" } },
  { id: "sales", name: { ar: "المبيعات", en: "Sales" } },
  { id: "admin", name: { ar: "الإدارة", en: "Administration" } },
];
export const DEFAULT_COST_CENTRE = "operations";

/** One event in a document's activity log. */
export interface ActivityEvent {
  kind: ActivityKind;
  /** ISO date or date-time. */
  at: string;
  by: UserId | "AI";
  note?: string;
  /** The other record involved: a payment, a bank line, a file. */
  ref?: string;
}

/** A supplier bill, or a supplier credit note (`kind: "CREDIT"`). */
export interface Bill {
  id: string;
  kind: "BILL" | "CREDIT";
  supplierId: string;
  /** The supplier's own number; unique per supplier. */
  supplierRef: string;
  date: string;
  dueDate: string | null;
  /** When it reaches the books, if not on `date`. */
  accountingDate?: string;
  state: DocumentState;
  evidence: EvidenceState;
  branch: string;
  lines: readonly DocLine[];
  /** A foreign-currency bill: the supplier's amount and the rate frozen to JOD. */
  foreign?: { currency: "USD"; amount: Decimal; rate: Decimal; source: string; rateDate: string };
  /** Tax withheld when it is paid in full: fixture data, the rate rule is the backend's. */
  withholding?: Withholding;
  approvedBy?: UserId;
  approvedOn?: string;
}

export interface Withholding {
  base: Decimal;
  rate: Decimal;
  amount: Decimal;
  remitBy: string;
}

export interface Allocation {
  documentId: string;
  /** Bills and invoices positive; a supplier credit used on a payment negative. */
  amount: Decimal;
}

export interface Payment {
  id: string;
  supplierId: string;
  date: string;
  method: PaymentMethod;
  /** Where the money leaves: a bank or cash account, or 2110 for a cheque. */
  from: string;
  chequeNumber?: string;
  /** When a cheque can be cashed, if it is post-dated. */
  chequeDue?: string;
  state: PaymentState;
  preparedBy: UserId;
  preparedOn: string;
  allocations: readonly Allocation[];
  withholding?: Withholding;
  /** The money sent, when it is not what the allocations less withholding come to (an advance). */
  amount?: Decimal;
  approvedBy?: UserId;
  approvedOn?: string;
  releasedBy?: UserId;
  releasedOn?: string;
  /** A short payment: the rest of the bill kept open, or written off to an account. */
  short?: { documentId: string; choice: ShortChoice; amount: Decimal; account: string };
}

export interface Receipt {
  id: string;
  customerId: string;
  date: string;
  /** The bank or cash account the money lands in. */
  to: string;
  chequeNumber?: string;
  allocations: readonly Allocation[];
}

export interface Invoice {
  id: string;
  /** Null until it is posted. */
  number: string | null;
  customerId: string;
  date: string;
  dueDate: string;
  state: DocumentState;
  terms: "CREDIT" | "CASH";
  buyerId: { type: BuyerIdType; value: string } | null;
  lines: readonly DocLine[];
  /** JoFotara accepts discounts per line only (S22's draft breaks the rule). */
  headerDiscount?: Decimal;
  /** The header discount spread across the lines: fixture data, the backend owns the rule. */
  headerDiscountSpread?: readonly Decimal[];
  /** A cash sale's buyer, when the customer is the generic cash customer. */
  buyerName?: string;
  fiscal: FiscalState;
  fiscalAttempts?: { made: number; max: number };
  /** When the fiscal state last changed (ISO date-time). */
  fiscalAt?: string;
  /** JoFotara's own words, quoted as sent. */
  authorityMessage?: string;
  /** What the rejection needs fixed. */
  fiscalFix?: FiscalFix;
  /** Assigned when it is prepared; a retry keeps it. */
  uuid?: string;
  /** The total in words (fixture data; the backend's renderer writes it). */
  words?: Bilingual;
  /** A credit note: a return invoice referencing the original, by line. */
  kind?: "CREDIT";
  creditOf?: string;
  /** The original's lines this credit note returns (each line once: quantities are whole). */
  returns?: readonly number[];
  /** Why the goods came back. */
  returnReason?: string;
}

export interface Cheque {
  number: string;
  direction: "RECEIVED" | "ISSUED";
  contactId: string;
  bank: Bilingual;
  amount: Decimal;
  dueDate: string;
  state: ChequeState;
  documentId?: string;
  returnedOn?: string;
  clearedOn?: string;
}

export interface JournalLine {
  account: string;
  /** The account was suggested from earlier entries; no person has chosen it yet. */
  suggested?: boolean;
  debit?: Decimal;
  credit?: Decimal;
  contactId?: string;
  description?: Bilingual;
  branch?: string;
  costCentre?: string;
}

/** Leave to post into a soft-closed month: why, who asked, who approved, and until when. */
export interface LockException {
  reason: Bilingual;
  requestedBy: UserId;
  approvedBy?: UserId;
  expiresOn: string;
}
/** How long a lock exception lasts. */
export const LOCK_EXCEPTION_DAYS = 7;
/** Does the exception still allow posting today? */
export const exceptionValid = (exception: LockException | undefined): boolean =>
  exception !== undefined && TODAY <= exception.expiresOn;

export interface Journal {
  id: string;
  number: string | null;
  date: string;
  postedOn?: string;
  state: JournalState;
  narration: Bilingual;
  lines: readonly JournalLine[];
  book?: JournalBook;
  reference?: string;
  /** The owner's easy entry this entry posts. */
  easyEntryId?: string;
  reverses?: string;
  reversedBy?: string;
  /** Posting into a soft-closed period: the reason is recorded, and who approved it if anyone did. */
  lockException?: LockException;
}

/** An owner's money event with no document, waiting for the accountant (S25). */
export interface EasyEntry {
  id: string;
  type: EasyEntryType;
  from: string | null;
  to: string | null;
  amount: Decimal;
  date: string;
  note: Bilingual;
  createdBy: UserId;
  state: "IN_REVIEW" | "POSTED";
  /** The slip's file name, if one was added. */
  slip?: string;
  /** The entry it posted as, once approved. */
  journalId?: string;
}

/** What an easy entry posts (fixture postings: the backend owns the real cases). */
export function easyEntryLines(entry: EasyEntry): JournalLine[] {
  const debit = entry.type === "OWNER_OUT" ? "3102" : (entry.to ?? "");
  const credit = entry.type === "OWNER_IN" ? "3102" : (entry.from ?? "");
  return [
    { account: debit, debit: entry.amount },
    { account: credit, credit: entry.amount },
  ];
}

/** How a statement line is settled: matched to records, or a new entry on an account. */
export interface Handling {
  kind: "match" | "create";
  /** A payment, receipt, invoice or cheque number to match; an account code to create on. */
  ref: string;
  /** Signed like the line. */
  amount: Decimal;
}

export interface StatementLine {
  id: string;
  date: string;
  /** The bank's own text. */
  text: string;
  reference?: string;
  /** Received is positive, spent is negative. */
  amount: Decimal;
  state: ReconLineState;
  why?: Bilingual;
  /** How the line is settled once the accountant handles it. */
  handling: readonly Handling[];
}

export interface Statement {
  bankAccountId: string;
  from: string;
  to: string;
  opening: Decimal;
  closing: Decimal;
  assignee: UserId;
  dueOn: string;
  lines: readonly StatementLine[];
}

/** Where a field sits on a page: fractions (0–1) of the page image. */
export interface FieldBox {
  /** Index into the item's `pages`. */
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
}

/** One page image of a document, with its intrinsic size in pixels. */
export interface DocPage {
  src: string;
  width: number;
  height: number;
  alt: Bilingual;
}

export interface FieldRead {
  /**
   * A header field (`supplier`, `supplierRef`, `date`, `dueDate`, `total`,
   * `iban`), a line (`lines.<n>`, from 0) or a line's part (`lines.<n>.quantity`).
   */
  field: string;
  value: string;
  band: Band;
  origin: Origin;
  /** The text as it appears on the document. */
  raw?: string;
  alternatives?: readonly string[];
  /** Where it was read. A field not on the document has none. */
  box?: FieldBox;
}

/** One incoming supplier document and the AI's proposal for it. */
export interface IntakeItem {
  id: string;
  scenario?: string;
  state: IntakeState;
  channel: "EMAIL" | "UPLOAD";
  sender?: string;
  fileName: string;
  receivedAt: string;
  readFrom?: "EINVOICE_QR" | "DOCUMENT";
  qr?: QrState;
  supplierId: string | null;
  /** The name as read, when no supplier matched. */
  supplierName?: Bilingual;
  supplierRef?: string;
  date?: string;
  dueDate?: string;
  currency: "JOD" | "USD";
  lines?: readonly DocLine[];
  foreign?: Bill["foreign"];
  evidence: EvidenceState;
  /** The lines as the e-invoice QR states them, when they differ from the ones read (S9). */
  qrLines?: readonly DocLine[];
  reasons: readonly ReasonCode[];
  pages?: readonly DocPage[];
  reads?: readonly FieldRead[];
  duplicateOf?: string;
  billId?: string;
  rejectReason?: RejectReason;
  /** The question asked when the item was handed to someone else. */
  note?: Bilingual;
}

/** One thing waiting for a person, with its typed reason. */
export interface WorkItem {
  id: string;
  scenario?: string;
  reason: ReasonCode;
  /** Raised by an AI rule, or by a person. */
  source: "AI" | UserId;
  assignee: UserId | null;
  createdOn: string;
  dueOn: string;
  /** The record it is about. */
  ref: string;
  amount?: Decimal;
  message?: Bilingual;
  /** The owner's answer to a question, once given. */
  answer?: string;
}

/** Money in and out of cash and bank in one month (`YYYY-MM`). */
export interface CashMonth {
  month: string;
  in: Decimal;
  out: Decimal;
}

export interface Dataset {
  bankAccounts: readonly BankAccount[];
  suppliers: readonly Supplier[];
  customers: readonly Customer[];
  bills: readonly Bill[];
  payments: readonly Payment[];
  receipts: readonly Receipt[];
  invoices: readonly Invoice[];
  cheques: readonly Cheque[];
  journals: readonly Journal[];
  easyEntries: readonly EasyEntry[];
  statements: readonly Statement[];
  intake: readonly IntakeItem[];
  workItems: readonly WorkItem[];
  /** The months before the books moved here: the old system's monthly totals. */
  cashHistory: readonly CashMonth[];
}

// ---------------------------------------------------------------------------
// Al-Nada General Trading
// ---------------------------------------------------------------------------

const line = (
  description: [string, string],
  account: string,
  quantity: Decimal,
  unitPrice: Decimal,
  amount: Decimal,
  taxCode: TaxCode,
  tax: Decimal,
): DocLine => ({
  description: { en: description[0], ar: description[1] },
  account,
  quantity,
  unitPrice,
  amount,
  taxCode,
  tax,
});

const PACKAGING_SALE: [string, string] = ["Packaging supplies", "مستلزمات تغليف"];
const sale = (amount: Decimal, tax: Decimal): DocLine =>
  line(PACKAGING_SALE, "4101", "1", amount, amount, "S", tax);

// S1 and S2: the same Petra bill, uploaded twice.
const PETRA_7731_LINES: readonly DocLine[] = [
  line(["Cartons 40x30x30", "كراتين 40×30×30"], "5150", "500", "0.420", "210.000", "S", "33.600"),
  line(["Stretch film", "نايلون تغليف مطاطي"], "5150", "24", "6.750", "162.000", "S", "25.920"),
  line(["Packing tape", "شريط لاصق للتغليف"], "5150", "120", "0.850", "102.000", "S", "16.320"),
];

const ORANGE_MONTHLY: readonly DocLine[] = [
  line(
    ["Mobile and internet, October 2026", "الخلوي والإنترنت، تشرين الأول 2026"],
    "5130",
    "1",
    "56.000",
    "56.000",
    "S",
    "8.960",
  ),
];

// The document images (docs/*.svg) share one page layout per direction, so a
// field's box is a fixed spot on the page: fractions of a 600 × 848 page.
const PAGE_W = 600;
const PAGE_H = 848;
const at = (x: number, y: number, w: number, h: number): FieldBox => ({
  page: 0,
  x: x / PAGE_W,
  y: y / PAGE_H,
  w: w / PAGE_W,
  h: h / PAGE_H,
});

interface Layout {
  fields: Readonly<
    Record<"supplier" | "supplierRef" | "date" | "dueDate" | "total" | "iban", FieldBox>
  >;
  line: (index: number) => FieldBox;
  quantity: (index: number) => FieldBox;
}

/** Documents laid out left to right (labels at the left). */
const LTR: Layout = {
  fields: {
    supplier: at(34, 48, 360, 34),
    supplierRef: at(184, 130, 200, 28),
    date: at(184, 162, 200, 28),
    dueDate: at(184, 194, 200, 30),
    total: at(430, 514, 136, 32),
    iban: at(104, 572, 400, 28),
  },
  line: (i) => at(34, 316 + 34 * i, 532, 28),
  quantity: (i) => at(296, 316 + 34 * i, 70, 28),
};

/** Arabic documents laid out right to left (labels at the right). */
const RTL: Layout = {
  fields: {
    supplier: at(206, 48, 360, 34),
    supplierRef: at(216, 130, 200, 28),
    date: at(216, 162, 200, 28),
    dueDate: at(216, 194, 200, 30),
    total: at(34, 514, 136, 32),
    iban: at(96, 572, 400, 28),
  },
  line: (i) => at(34, 316 + 34 * i, 532, 28),
  quantity: (i) => at(234, 316 + 34 * i, 70, 28),
};

function boxFor(layout: Layout, field: string): FieldBox | undefined {
  const lineField = /^lines\.(\d+)(\.quantity)?$/.exec(field);
  if (lineField !== null) {
    const index = Number(lineField[1]);
    return lineField[2] === undefined ? layout.line(index) : layout.quantity(index);
  }
  return field in layout.fields ? layout.fields[field as keyof Layout["fields"]] : undefined;
}

/**
 * The AI's reads of a document: every field it found, Clear and read from the
 * page unless `overrides` says otherwise, each with its box on the page. An
 * override for a field not in `values` adds that read (a line's quantity).
 */
function docReads(
  layout: Layout,
  values: {
    supplier: string;
    supplierRef: string;
    date: string;
    dueDate?: string;
    lines: readonly string[];
    total: Decimal;
    iban?: string;
  },
  overrides: Readonly<Record<string, Partial<FieldRead>>> = {},
): FieldRead[] {
  const { lines, ...header } = values;
  const found: [string, string][] = [
    ...Object.entries(header),
    ...lines.map((text, index): [string, string] => [`lines.${index}`, text]),
  ];
  const extra = Object.keys(overrides)
    .filter((field) => !found.some(([name]) => name === field))
    .map((field): [string, string] => [field, ""]);
  return [...found, ...extra].map(([field, value]): FieldRead => {
    const box = boxFor(layout, field);
    return {
      field,
      value,
      band: "CLEAR",
      origin: "READ",
      ...(box !== undefined ? { box } : {}),
      ...overrides[field],
    };
  });
}

const page = (src: string, en: string, ar: string): DocPage => ({
  src,
  width: PAGE_W,
  height: PAGE_H,
  alt: { en, ar },
});

// S1: the due date is handwritten in Arabic-Indic digits; line 2's quantity could be 21.
const PETRA_7731_READS = docReads(
  LTR,
  {
    supplier: "PETRA PACKAGING",
    supplierRef: "7731",
    date: "2026-10-02",
    dueDate: "2026-10-16",
    lines: ["Cartons 40x30x30", "Stretch film", "Packing tape"],
    total: "549.840",
  },
  {
    date: { raw: "02/10/2026" },
    dueDate: { band: "CHECK", raw: "١٦/١٠" },
    "lines.1.quantity": { value: "24", band: "NOT_SURE", raw: "24", alternatives: ["21"] },
    total: { origin: "CALCULATED" },
  },
);

const NADA_BILLS: readonly Bill[] = [
  {
    id: "BL-2026-0078",
    kind: "BILL",
    supplierId: "petra",
    supplierRef: "7690",
    date: "2026-08-12",
    dueDate: "2026-08-26",
    state: "POSTED",
    evidence: "EINVOICE_PRESENT",
    branch: "amman-main",
    lines: [
      line(
        ["Cartons 40x30x30", "كراتين 40×30×30"],
        "5150",
        "1",
        "551.724",
        "551.724",
        "S",
        "88.276",
      ),
    ],
  },
  {
    id: "BL-2026-0079",
    kind: "BILL",
    supplierId: "petra",
    supplierRef: "7702",
    date: "2026-08-20",
    dueDate: "2026-09-03",
    state: "POSTED",
    evidence: "EINVOICE_PRESENT",
    branch: "amman-main",
    lines: [
      line(
        ["Stretch film", "نايلون تغليف مطاطي"],
        "5150",
        "1",
        "905.172",
        "905.172",
        "S",
        "144.828",
      ),
    ],
  },
  {
    id: "BL-2026-0080",
    kind: "BILL",
    supplierId: "alnoor",
    supplierRef: "R-2026-09",
    date: "2026-09-01",
    dueDate: "2026-09-05",
    state: "POSTED",
    evidence: "NOT_APPLICABLE",
    branch: "amman-main",
    lines: [
      line(
        ["Warehouse rent, September 2026", "إيجار المستودع، أيلول 2026"],
        "5120",
        "1",
        "850.000",
        "850.000",
        "O",
        ZERO,
      ),
    ],
  },
  {
    id: "BL-2026-0081",
    kind: "BILL",
    supplierId: "globaloffice",
    supplierRef: "GOS-5480",
    date: "2026-09-08",
    dueDate: "2026-09-22",
    state: "POSTED",
    evidence: "EINVOICE_PRESENT",
    branch: "amman-main",
    lines: [
      line(
        ["Shipping labels and bubble wrap", "ملصقات شحن وأغلفة فقاعية"],
        "5150",
        "1",
        "737.931",
        "737.931",
        "S",
        "118.069",
      ),
    ],
  },
  {
    id: "BL-2026-0082",
    kind: "BILL",
    supplierId: "alsharq",
    supplierRef: "AS-3310",
    date: "2026-09-03",
    dueDate: "2026-09-17",
    state: "POSTED",
    evidence: "EINVOICE_PRESENT",
    branch: "zarqa",
    lines: [
      line(
        ["Packing cartons, mixed", "كراتين تغليف متنوعة"],
        "5150",
        "1000",
        "0.950",
        "950.000",
        "S",
        "152.000",
      ),
      line(["Strapping tape", "شريط تحزيم"], "5150", "50", "6.500", "325.000", "S", "52.000"),
    ],
  },
  {
    id: "BL-2026-0084",
    kind: "BILL",
    supplierId: "orange",
    supplierRef: "64-5512",
    date: "2026-09-28",
    dueDate: "2026-10-12",
    state: "POSTED",
    evidence: "EINVOICE_PRESENT",
    branch: "amman-main",
    lines: [
      line(
        ["Mobile and internet, September 2026", "الخلوي والإنترنت، أيلول 2026"],
        "5130",
        "1",
        "56.000",
        "56.000",
        "S",
        "8.960",
      ),
    ],
  },
  {
    id: "BL-2026-0085",
    kind: "BILL",
    supplierId: "petra",
    supplierRef: "7715",
    date: "2026-09-12",
    dueDate: "2026-09-26",
    state: "POSTED",
    evidence: "EINVOICE_PRESENT",
    branch: "amman-main",
    lines: [
      line(["Packing tape", "شريط لاصق للتغليف"], "5150", "1", "275.862", "275.862", "S", "44.138"),
    ],
  },
  {
    // S6's September shipment: paid 29 Sep from a bank line 3.540 higher.
    id: "BL-2026-0086",
    kind: "BILL",
    supplierId: "shenzhen",
    supplierRef: "SB-2409",
    date: "2026-09-21",
    dueDate: "2026-09-30",
    state: "POSTED",
    evidence: "NOT_APPLICABLE",
    branch: "amman-main",
    foreign: {
      currency: "USD",
      amount: "2400.00",
      rate: "0.709000",
      source: "CBJ",
      rateDate: "2026-09-21",
    },
    lines: [
      line(
        ["Printed mailer boxes", "علب شحن مطبوعة"],
        "5150",
        "1",
        "1701.600",
        "1701.600",
        "O",
        ZERO,
      ),
    ],
  },
  {
    // S18: withholding 5% is taken at payment.
    id: "BL-2026-0089",
    kind: "BILL",
    supplierId: "aladwan",
    supplierRef: "ADW-1187",
    date: "2026-10-01",
    dueDate: "2026-10-15",
    state: "POSTED",
    evidence: "EINVOICE_PRESENT",
    branch: "amman-main",
    withholding: { base: "500.000", rate: "5", amount: "25.000", remitBy: "2026-11-08" },
    lines: [
      line(
        ["Legal retainer, September 2026", "أتعاب قانونية شهرية، أيلول 2026"],
        "5140",
        "1",
        "500.000",
        "500.000",
        "S",
        "80.000",
      ),
    ],
  },
  {
    // S17: paid short, 1,000.000 of 1,050.000.
    id: "BL-2026-0090",
    kind: "BILL",
    supplierId: "alsharq",
    supplierRef: "AS-3355",
    date: "2026-09-24",
    dueDate: "2026-10-08",
    state: "POSTED",
    evidence: "EINVOICE_PRESENT",
    branch: "zarqa",
    lines: [
      line(
        ["Packing cartons, mixed", "كراتين تغليف متنوعة"],
        "5150",
        "1",
        "905.172",
        "905.172",
        "S",
        "144.828",
      ),
    ],
  },
  {
    id: "BL-2026-0093",
    kind: "BILL",
    supplierId: "petra",
    supplierRef: "7728",
    date: "2026-10-02",
    dueDate: "2026-10-16",
    state: "POSTED",
    evidence: "EINVOICE_PRESENT",
    branch: "amman-main",
    lines: [
      line(
        ["Cartons 40x30x30", "كراتين 40×30×30"],
        "5150",
        "1250",
        "0.420",
        "525.000",
        "S",
        "84.000",
      ),
      line(["Stretch film", "نايلون تغليف مطاطي"], "5150", "60", "6.750", "405.000", "S", "64.800"),
      line(["Packing tape", "شريط لاصق للتغليف"], "5150", "150", "0.800", "120.000", "S", "19.200"),
    ],
  },
  {
    // S13: an open credit, used on PAY-2026-0047.
    id: "CN-P-0007",
    kind: "CREDIT",
    supplierId: "petra",
    supplierRef: "CR-7719",
    date: "2026-09-20",
    dueDate: null,
    state: "POSTED",
    evidence: "EINVOICE_PRESENT",
    branch: "amman-main",
    lines: [
      line(
        ["Damaged cartons returned", "كراتين تالفة مُرجعة"],
        "5150",
        "1",
        "51.724",
        "51.724",
        "S",
        "8.276",
      ),
    ],
  },
  {
    // S14: approved by Samer on 8 Oct, waiting for its accounting date.
    id: "D-0007",
    kind: "BILL",
    supplierId: "alnoor",
    supplierRef: "R-2026-11",
    date: "2026-10-08",
    dueDate: "2026-11-01",
    accountingDate: "2026-10-31",
    state: "APPROVED",
    evidence: "NOT_APPLICABLE",
    branch: "amman-main",
    approvedBy: "samer",
    approvedOn: "2026-10-08",
    lines: [
      line(
        ["Warehouse rent, November 2026", "إيجار المستودع، تشرين الثاني 2026"],
        "5120",
        "1",
        "850.000",
        "850.000",
        "O",
        ZERO,
      ),
    ],
  },
];

const NADA_TRADING: Dataset = {
  bankAccounts: [
    {
      id: "arab-bank",
      account: "1102",
      name: { ar: "البنك العربي – جاري", en: "Arab Bank – current" },
      reconciledTo: "2026-08-31",
    },
    {
      id: "housing-bank",
      account: "1103",
      name: { ar: "بنك الإسكان", en: "Housing Bank" },
      reconciledTo: "2026-08-31",
    },
    {
      id: "cash-box",
      account: "1101",
      name: { ar: "الصندوق", en: "Cash box" },
      reconciledTo: null,
    },
  ],

  suppliers: [
    {
      id: "petra",
      name: { ar: "بترا للتغليف", en: "Petra Packaging" },
      currency: "JOD",
      email: "ap@petra-packaging.jo",
      bankAccount: {
        bank: { ar: "البنك العربي", en: "Arab Bank" },
        number: "JO71ARAB1210000000121000789012",
        verifiedOn: "2026-03-03",
      },
    },
    {
      id: "orange",
      name: { ar: "أورنج الأردن", en: "Orange Jordan" },
      currency: "JOD",
      bankAccount: {
        bank: { ar: "بنك الإسكان", en: "Housing Bank" },
        number: "JO15HBHO0320000000032000045678",
        verifiedOn: "2025-11-20",
      },
    },
    {
      id: "alsharq",
      name: { ar: "الشرق للتوريدات", en: "Al-Sharq Supplies" },
      currency: "JOD",
      bankAccount: {
        bank: { ar: "بنك القاهرة عمّان", en: "Cairo Amman Bank" },
        number: "JO09CAAB1010000000101000234567",
        verifiedOn: "2025-06-14",
      },
    },
    {
      id: "shenzhen",
      name: { ar: "شنتشن برايتواي للتغليف", en: "Shenzhen Brightway Packaging Co." },
      currency: "USD",
      bankAccount: {
        bank: { ar: "بنك الصين، شنتشن", en: "Bank of China, Shenzhen" },
        number: "6216610000012345678",
        verifiedOn: "2026-02-10",
      },
    },
    {
      id: "alamal",
      name: { ar: "مطبعة الأمل", en: "Al-Amal Printing" },
      currency: "JOD",
      bankAccount: null,
    },
    {
      id: "alnoor",
      name: { ar: "النور العقارية", en: "Al-Noor Real Estate" },
      currency: "JOD",
      bankAccount: {
        bank: { ar: "البنك الإسلامي الأردني", en: "Jordan Islamic Bank" },
        number: "JO62JIBA1020000000102000774410",
        verifiedOn: "2025-01-12",
      },
    },
    {
      id: "aladwan",
      name: { ar: "مكتب العدوان للمحاماة", en: "Al-Adwan Law Office" },
      currency: "JOD",
      bankAccount: {
        bank: { ar: "بنك الاتحاد", en: "Bank al Etihad" },
        number: "JO27UBSI0010000000001000556677",
        verifiedOn: "2025-09-01",
      },
    },
    {
      id: "globaloffice",
      name: { ar: "التوريدات المكتبية العالمية", en: "Global Office Supplies" },
      currency: "JOD",
      bankAccount: {
        bank: { ar: "البنك الأهلي الأردني", en: "Jordan Ahli Bank" },
        number: "JO53JONB1000000000100000998811",
        verifiedOn: "2024-12-05",
      },
    },
  ],

  customers: [
    {
      id: "alsalam",
      name: { ar: "سوبرماركت السلام", en: "Al-Salam Supermarket" },
      taxNumber: "11873201",
    },
    { id: "diyafa", name: { ar: "مطاعم ضيافة", en: "Diyafa Restaurants" }, taxNumber: null },
    { id: "alreem", name: { ar: "فندق الريم", en: "Al-Reem Hotel" }, taxNumber: "12450987" },
    { id: "alofoq", name: { ar: "الأفق التجارية", en: "Al-Ofoq Trading" }, taxNumber: "10988342" },
    { id: "cash", name: { ar: "عميل نقدي", en: "Cash customer" }, taxNumber: null },
  ],

  bills: NADA_BILLS,
  payments: [
    {
      id: "PAY-2026-0041",
      supplierId: "petra",
      date: "2026-09-15",
      method: "CHEQUE",
      from: "2110",
      chequeNumber: "000412",
      state: "RELEASED",
      preparedBy: "layla",
      preparedOn: "2026-09-14",
      allocations: [{ documentId: "BL-2026-0078", amount: "640.000" }],
    },
    {
      id: "PAY-2026-0042",
      supplierId: "petra",
      date: "2026-09-04",
      method: "BANK_TRANSFER",
      from: "1102",
      state: "RELEASED",
      preparedBy: "layla",
      preparedOn: "2026-09-03",
      allocations: [{ documentId: "BL-2026-0079", amount: "1050.000" }],
    },
    {
      id: "PAY-2026-0043",
      supplierId: "alnoor",
      date: "2026-09-07",
      method: "BANK_TRANSFER",
      from: "1102",
      state: "SETTLED",
      preparedBy: "samer",
      preparedOn: "2026-09-06",
      allocations: [{ documentId: "BL-2026-0080", amount: "850.000" }],
    },
    {
      id: "PAY-2026-0044",
      supplierId: "alsharq",
      date: "2026-09-22",
      method: "BANK_TRANSFER",
      from: "1102",
      state: "RELEASED",
      preparedBy: "layla",
      preparedOn: "2026-09-21",
      allocations: [{ documentId: "BL-2026-0082", amount: "1479.000" }],
    },
    {
      id: "PAY-2026-0045",
      supplierId: "globaloffice",
      date: "2026-09-22",
      method: "BANK_TRANSFER",
      from: "1102",
      state: "RELEASED",
      preparedBy: "layla",
      preparedOn: "2026-09-21",
      allocations: [{ documentId: "BL-2026-0081", amount: "856.000" }],
    },
    {
      id: "PAY-2026-0046",
      supplierId: "shenzhen",
      date: "2026-09-29",
      method: "BANK_TRANSFER",
      from: "1102",
      state: "RELEASED",
      preparedBy: "layla",
      preparedOn: "2026-09-28",
      allocations: [{ documentId: "BL-2026-0086", amount: "1701.600" }],
    },
    {
      // S15: prepared by Layla, so Samer releases it while dual control is on.
      id: "PAY-2026-0047",
      supplierId: "petra",
      date: "2026-10-09",
      method: "BANK_TRANSFER",
      from: "1102",
      state: "DRAFT",
      preparedBy: "layla",
      preparedOn: "2026-10-08",
      allocations: [
        { documentId: "BL-2026-0085", amount: "320.000" },
        { documentId: "BL-2026-0093", amount: "1218.000" },
        { documentId: "CN-P-0007", amount: "-60.000" },
      ],
    },
    {
      // S16: prepared by Samer, so Samer cannot release it while dual control is on.
      id: "PAY-2026-0048",
      supplierId: "orange",
      date: "2026-10-09",
      method: "BANK_TRANSFER",
      from: "1102",
      state: "DRAFT",
      preparedBy: "samer",
      preparedOn: "2026-10-08",
      allocations: [{ documentId: "BL-2026-0084", amount: "64.960" }],
    },
  ],

  receipts: [
    {
      id: "RC-2026-0064",
      customerId: "alofoq",
      date: "2026-08-28",
      to: "1102",
      chequeNumber: "104578",
      allocations: [{ documentId: "INV-2026-0098", amount: "2150.000" }],
    },
    {
      id: "RC-2026-0071",
      customerId: "alsalam",
      date: "2026-09-02",
      to: "1102",
      allocations: [{ documentId: "INV-2026-0109", amount: "3480.000" }],
    },
  ],

  invoices: [
    {
      // S19: settled by cheque 104578, which comes back on 10 Sep.
      id: "INV-2026-0098",
      words: { en: "Two thousand one hundred fifty dinars", ar: "ألفان ومائة وخمسون ديناراً" },
      number: "INV-2026-0098",
      customerId: "alofoq",
      date: "2026-08-05",
      dueDate: "2026-08-19",
      state: "POSTED",
      terms: "CREDIT",
      buyerId: { type: "TN", value: "10988342" },
      lines: [sale("1853.448", "296.552")],
      fiscal: "ACCEPTED",
      uuid: "4f1c2a90-7d3e-4b8a-9c51-2e6f0a7b3d18",
    },
    {
      id: "INV-2026-0109",
      words: {
        en: "Three thousand four hundred eighty dinars",
        ar: "ثلاثة آلاف وأربعمائة وثمانون ديناراً",
      },
      number: "INV-2026-0109",
      customerId: "alsalam",
      date: "2026-08-25",
      dueDate: "2026-09-08",
      state: "POSTED",
      terms: "CREDIT",
      buyerId: { type: "TN", value: "11873201" },
      lines: [sale("3000.000", "480.000")],
      fiscal: "ACCEPTED",
      uuid: "8b2d4e61-3a9f-4c07-b8e2-5d1a6f9c0e47",
    },
    {
      // S20: Diyafa pays 1,160.000 of it.
      id: "INV-2026-0112",
      words: {
        en: "One thousand two hundred seventy-six dinars",
        ar: "ألف ومائتان وستة وسبعون ديناراً",
      },
      number: "INV-2026-0112",
      customerId: "diyafa",
      date: "2026-09-03",
      dueDate: "2026-09-17",
      state: "POSTED",
      terms: "CREDIT",
      buyerId: { type: "PN", value: "JO-4821770" },
      lines: [sale("1100.000", "176.000")],
      fiscal: "ACCEPTED",
      uuid: "c7e9a3b2-1f54-4d6e-a0b8-9e2c7d4f1a63",
    },
    {
      id: "INV-2026-0117",
      words: { en: "Two thousand nine hundred dinars", ar: "ألفان وتسعمائة دينار" },
      number: "INV-2026-0117",
      customerId: "alsalam",
      date: "2026-09-18",
      dueDate: "2026-10-02",
      state: "POSTED",
      terms: "CREDIT",
      buyerId: { type: "TN", value: "11873201" },
      lines: [sale("2500.000", "400.000")],
      fiscal: "ACCEPTED",
      uuid: "1a5f8c3d-6e2b-4b90-8d7a-3c4e9f0b2d85",
    },
    {
      // S20 and S22: paid as two CliQ lines; accepted with warnings.
      id: "INV-2026-0120",
      words: { en: "Eleven thousand six hundred dinars", ar: "أحد عشر ألفاً وستمائة دينار" },
      number: "INV-2026-0120",
      customerId: "alreem",
      date: "2026-09-15",
      dueDate: "2026-09-29",
      state: "POSTED",
      terms: "CREDIT",
      buyerId: { type: "TN", value: "12450987" },
      lines: [sale("10000.000", "1600.000")],
      fiscal: "ACCEPTED_WITH_WARNINGS",
      authorityMessage: "Buyer address line exceeds the recommended length.",
      uuid: "e3b8d1f6-2c7a-4e05-9a1d-7f6c3b8e0d24",
    },
    {
      // S21: a credit invoice with no buyer ID.
      id: "INV-2026-0121",
      words: { en: "Nine hundred twenty-eight dinars", ar: "تسعمائة وثمانية وعشرون ديناراً" },
      number: "INV-2026-0121",
      customerId: "diyafa",
      date: "2026-10-08",
      dueDate: "2026-11-07",
      state: "POSTED",
      terms: "CREDIT",
      buyerId: null,
      lines: [sale("800.000", "128.000")],
      fiscal: "REJECTED",
      fiscalAttempts: { made: 1, max: 1 },
      fiscalAt: "2026-10-08T14:02:00+03:00",
      authorityMessage: "Buyer identification is required for receivable invoices.",
      fiscalFix: "BUYER_ID",
      uuid: "9d4e2b71-5c8a-4f36-b1e0-6a7c3d9f2e58",
    },
    {
      // S22: no answer from JoFotara yet; not a rejection.
      id: "INV-2026-0122",
      words: {
        en: "Two thousand three hundred twenty dinars",
        ar: "ألفان وثلاثمائة وعشرون ديناراً",
      },
      number: "INV-2026-0122",
      customerId: "alreem",
      date: "2026-10-07",
      dueDate: "2026-11-06",
      state: "POSTED",
      terms: "CREDIT",
      buyerId: { type: "TN", value: "12450987" },
      lines: [sale("2000.000", "320.000")],
      fiscal: "UNKNOWN_OUTCOME",
      fiscalAt: "2026-10-07T16:40:00+03:00",
      uuid: "2b7f9c40-8e13-4a5d-9f62-0c4e8b1d7a39",
    },
    {
      // JoFotara outage: only a watermarked pending copy can be printed.
      id: "INV-2026-0123",
      words: { en: "One thousand seven hundred forty dinars", ar: "ألف وسبعمائة وأربعون ديناراً" },
      number: "INV-2026-0123",
      customerId: "alsalam",
      date: "2026-10-09",
      dueDate: "2026-10-23",
      state: "POSTED",
      terms: "CREDIT",
      buyerId: { type: "TN", value: "11873201" },
      lines: [sale("1500.000", "240.000")],
      fiscal: "RETRYING",
      fiscalAttempts: { made: 2, max: 5 },
      fiscalAt: "2026-10-09T09:15:00+03:00",
      uuid: "6c1a8e35-0f7b-4d92-a4c6-3e9b5f2a8d17",
    },
    {
      // S22: a draft with a header discount, which the pre-submission check catches.
      id: "INV-DRAFT-01",
      words: {
        en: "Three hundred ninety-seven dinars and 880 fils",
        ar: "ثلاثمائة وسبعة وتسعون ديناراً وثمانمائة وثمانون فلساً",
      },
      number: null,
      customerId: "alsalam",
      date: "2026-10-09",
      dueDate: "2026-10-23",
      state: "DRAFT",
      terms: "CREDIT",
      buyerId: { type: "TN", value: "11873201" },
      // The tax is on the discounted lines; the spread keeps the same total.
      lines: [sale("150.000", "23.520"), sale("200.000", "31.360")],
      headerDiscount: "7.000",
      headerDiscountSpread: ["3.000", "4.000"],
      fiscal: "REQUIRED",
    },
  ],

  cheques: [
    {
      number: "220145",
      direction: "RECEIVED",
      contactId: "alsalam",
      bank: { ar: "البنك العربي", en: "Arab Bank" },
      amount: "2400.000",
      dueDate: "2026-10-15",
      state: "IN_SAFE",
    },
    {
      number: "220146",
      direction: "RECEIVED",
      contactId: "alsalam",
      bank: { ar: "البنك العربي", en: "Arab Bank" },
      amount: "2400.000",
      dueDate: "2026-11-15",
      state: "IN_SAFE",
    },
    {
      number: "330019",
      direction: "RECEIVED",
      contactId: "diyafa",
      bank: { ar: "بنك الإسكان", en: "Housing Bank" },
      amount: "2550.000",
      dueDate: "2026-10-20",
      state: "UNDER_COLLECTION",
    },
    {
      number: "104578",
      direction: "RECEIVED",
      contactId: "alofoq",
      bank: { ar: "بنك الأردن", en: "Bank of Jordan" },
      amount: "2150.000",
      dueDate: "2026-08-28",
      state: "RETURNED",
      documentId: "INV-2026-0098",
      returnedOn: "2026-09-10",
    },
    {
      number: "000412",
      direction: "ISSUED",
      contactId: "petra",
      bank: { ar: "البنك العربي", en: "Arab Bank" },
      amount: "640.000",
      dueDate: "2026-09-15",
      state: "CLEARED",
      documentId: "PAY-2026-0041",
      clearedOn: "2026-09-30",
    },
    {
      number: "000413",
      direction: "ISSUED",
      contactId: "alnoor",
      bank: { ar: "البنك العربي", en: "Arab Bank" },
      amount: "850.000",
      dueDate: "2026-11-01",
      state: "ISSUED",
      documentId: "D-0007",
    },
  ],

  journals: [
    {
      id: "JE-2026-0001",
      number: "JE-2026-0001",
      date: "2026-07-31",
      postedOn: "2026-08-01",
      state: "POSTED",
      narration: { ar: "أرصدة مدوّرة", en: "Balances brought forward" },
      lines: [
        { account: "1101", debit: "640.500" },
        { account: "1102", debit: "22160.250" },
        { account: "1103", debit: "8215.000" },
        { account: "3101", credit: "25000.000" },
        { account: "3102", credit: "6015.750" },
      ],
    },
    {
      id: "JE-2026-0402",
      number: "JE-2026-0402",
      date: "2026-08-14",
      postedOn: "2026-08-14",
      state: "POSTED",
      narration: { ar: "وقود سيارة التوصيل، آب", en: "Delivery van fuel, August" },
      lines: [
        { account: "5190", debit: "85.000" },
        { account: "1101", credit: "85.000" },
      ],
    },
    {
      id: "JE-2026-0403",
      number: "JE-2026-0403",
      date: "2026-08-28",
      postedOn: "2026-08-28",
      state: "POSTED",
      narration: { ar: "فاتورة كهرباء آب", en: "Electricity bill, August" },
      lines: [
        { account: "5180", debit: "172.300" },
        { account: "1103", credit: "172.300" },
      ],
    },
    {
      id: "JE-2026-0424",
      number: "JE-2026-0424",
      date: "2026-10-02",
      postedOn: "2026-10-02",
      state: "POSTED",
      narration: { ar: "إيداع من المالك في بنك الإسكان", en: "Owner's deposit into Housing Bank" },
      lines: [
        { account: "1103", debit: "2000.000" },
        { account: "3102", credit: "2000.000" },
      ],
    },
    {
      id: "JE-2026-0427",
      number: "JE-2026-0427",
      date: "2026-10-05",
      postedOn: "2026-10-05",
      state: "POSTED",
      narration: { ar: "دفع كهرباء أيلول المستحقة", en: "September electricity paid" },
      lines: [
        { account: "2140", debit: "186.400" },
        { account: "1103", credit: "186.400" },
      ],
    },
    {
      id: "JE-2026-0429",
      number: "JE-2026-0429",
      date: "2026-10-06",
      postedOn: "2026-10-06",
      state: "POSTED",
      narration: { ar: "وقود سيارة التوصيل، تشرين الأول", en: "Delivery van fuel, October" },
      lines: [
        { account: "5190", debit: "60.000" },
        { account: "1101", credit: "60.000" },
      ],
    },
    {
      id: "JE-2026-0405",
      number: "JE-2026-0405",
      date: "2026-09-30",
      postedOn: "2026-10-01",
      state: "POSTED",
      narration: { ar: "كهرباء أيلول المستحقة", en: "Accrued electricity, September" },
      lines: [
        { account: "5180", debit: "186.400" },
        { account: "2140", credit: "186.400" },
      ],
    },
    {
      id: "JE-2026-0418",
      number: "JE-2026-0418",
      date: "2026-09-15",
      postedOn: "2026-09-15",
      state: "REVERSED",
      reversedBy: "JE-2026-0419",
      narration: { ar: "وقود سيارة التوصيل", en: "Fuel for the delivery van" },
      lines: [
        { account: "5190", debit: "95.000" },
        { account: "1101", credit: "95.000" },
      ],
    },
    {
      id: "JE-2026-0419",
      number: "JE-2026-0419",
      date: "2026-09-16",
      postedOn: "2026-09-16",
      state: "POSTED",
      reverses: "JE-2026-0418",
      narration: {
        ar: "عكس القيد JE-2026-0418: الوقود دُفع ببطاقة الوقود",
        en: "Reverses JE-2026-0418: the fuel card paid it",
      },
      lines: [
        { account: "1101", debit: "95.000" },
        { account: "5190", credit: "95.000" },
      ],
    },
    {
      // S24: dated in the soft-closed September, posted after the close pack.
      id: "JE-2026-0431",
      number: "JE-2026-0431",
      date: "2026-09-29",
      postedOn: "2026-10-07",
      state: "POSTED",
      narration: { ar: "أتعاب التدقيق المستحقة عن أيلول", en: "Accrued audit fee, September" },
      lines: [
        { account: "5140", debit: "310.000" },
        { account: "2140", credit: "310.000" },
      ],
      lockException: {
        reason: {
          ar: "وصلت فاتورة المدقق بعد إقفال حزمة أيلول",
          en: "The auditor's invoice arrived after the September pack",
        },
        requestedBy: "layla",
        approvedBy: "samer",
        expiresOn: "2026-10-14",
      },
    },
    {
      // S23: out of balance by 0.500 — the only unbalanced entry.
      id: "JE-DRAFT-S23",
      number: null,
      date: "2026-09-29",
      state: "DRAFT",
      narration: { ar: "إعادة تصنيف وقود أيلول", en: "Reclassify September fuel" },
      lines: [
        {
          account: "5190",
          suggested: true,
          debit: "145.500",
          description: { ar: "بطاقات وقود أيلول", en: "Sep fuel cards" },
        },
        {
          account: "1101",
          credit: "145.000",
          description: { ar: "دُفع نقداً", en: "Paid in cash" },
        },
      ],
    },
  ],

  easyEntries: [
    {
      // S25: Samer's own deposit, waiting for Layla.
      id: "EE-2026-0012",
      type: "OWNER_IN",
      from: null,
      to: "1102",
      amount: "5000.000",
      date: "2026-10-06",
      note: { ar: "تمويل شراء بضاعة تشرين الأول", en: "Funding October stock purchases" },
      createdBy: "samer",
      state: "IN_REVIEW",
    },
  ],

  statements: [
    {
      bankAccountId: "arab-bank",
      from: "2026-09-01",
      to: "2026-09-30",
      opening: "24310.250",
      closing: "33015.110",
      assignee: "layla",
      dueOn: "2026-10-12",
      lines: [
        {
          id: "L01",
          date: "2026-09-02",
          text: "CLIQ IN AL SALAM SUPERMARKET",
          reference: "INV-2026-0109",
          amount: "3480.000",
          state: "SUGGESTED_EXACT",
          why: {
            ar: "المبلغ نفسه، في اليوم نفسه، والمرجع رقم الفاتورة",
            en: "Same amount, same day, reference = invoice number",
          },
          handling: [{ kind: "match", ref: "RC-2026-0071", amount: "3480.000" }],
        },
        {
          id: "L02",
          date: "2026-09-04",
          text: "TRF OUT PETRA PACKAGING",
          amount: "-1050.000",
          state: "SUGGESTED_EXACT",
          why: { ar: "المبلغ والمورد والتاريخ متطابقة", en: "Amount, supplier and date match" },
          handling: [{ kind: "match", ref: "PAY-2026-0042", amount: "-1050.000" }],
        },
        {
          id: "L03",
          date: "2026-09-07",
          text: "TRF OUT AL NOOR REAL ESTATE",
          amount: "-850.000",
          state: "MATCHED",
          handling: [{ kind: "match", ref: "PAY-2026-0043", amount: "-850.000" }],
        },
        {
          // S19: the returned cheque reopens INV-2026-0098.
          id: "L04",
          date: "2026-09-10",
          text: "CHQ DEP RETURNED 104578 AL OFOQ",
          reference: "104578",
          amount: "-2150.000",
          state: "NEEDS_REVIEW",
          why: {
            ar: "رقم الشيك يطابق شيكاً مستلماً",
            en: "The cheque number matches a received cheque",
          },
          handling: [{ kind: "create", ref: "1120", amount: "-2150.000" }],
        },
        {
          id: "L05",
          date: "2026-09-10",
          text: "RETURNED CHQ FEE",
          amount: "-5.000",
          state: "NO_SUGGESTION",
          handling: [{ kind: "create", ref: "5160", amount: "-5.000" }],
        },
        {
          // S20: a short receipt against a 1,276.000 invoice.
          id: "L06",
          date: "2026-09-14",
          text: "CLIQ IN DIYAFA RESTAURANTS",
          amount: "1160.000",
          state: "NEEDS_REVIEW",
          why: {
            ar: "العميل نفسه، والمبلغ أقل من الفاتورة",
            en: "Same customer; less than the invoice",
          },
          handling: [{ kind: "match", ref: "INV-2026-0112", amount: "1160.000" }],
        },
        {
          // S20: one line paying two supplier payments.
          id: "L07",
          date: "2026-09-22",
          text: "TRF OUT BATCH 2 PAYMENTS",
          amount: "-2335.000",
          state: "NEEDS_REVIEW",
          why: { ar: "مجموع دفعتين في اليوم نفسه", en: "Two payments on the same day add up" },
          handling: [
            { kind: "match", ref: "PAY-2026-0044", amount: "-1479.000" },
            { kind: "match", ref: "PAY-2026-0045", amount: "-856.000" },
          ],
        },
        {
          // S20: a CliQ payment over JOD 10,000 arrives split in two.
          id: "L08",
          date: "2026-09-25",
          text: "CLIQ IN AL REEM HOTEL 1/2",
          amount: "6000.000",
          state: "NEEDS_REVIEW",
          handling: [{ kind: "match", ref: "INV-2026-0120", amount: "6000.000" }],
        },
        {
          id: "L09",
          date: "2026-09-25",
          text: "CLIQ IN AL REEM HOTEL 2/2",
          amount: "5600.000",
          state: "NEEDS_REVIEW",
          handling: [{ kind: "match", ref: "INV-2026-0120", amount: "5600.000" }],
        },
        {
          // S26: Layla asks Samer what this is; the proposal is money he put in.
          id: "L10",
          date: "2026-09-28",
          text: "CASH DEPOSIT",
          amount: "1200.000",
          state: "NEEDS_REVIEW",
          handling: [{ kind: "create", ref: "3102", amount: "1200.000" }],
        },
        {
          // S6: the bank's rate cost 3.540 more than the frozen one.
          id: "L11",
          date: "2026-09-29",
          text: "TRF OUT SHENZHEN BRIGHTWAY",
          amount: "-1705.140",
          state: "NEEDS_REVIEW",
          why: {
            ar: "الاسم والتاريخ متطابقان؛ الفرق 3.540 فرق عملة",
            en: "Name and date match; 3.540 left = FX",
          },
          handling: [
            { kind: "match", ref: "PAY-2026-0046", amount: "-1701.600" },
            { kind: "create", ref: "5170", amount: "-3.540" },
          ],
        },
        {
          id: "L12",
          date: "2026-09-30",
          text: "CHQ 000412 PETRA PACKAGING",
          reference: "000412",
          amount: "-640.000",
          state: "SUGGESTED_EXACT",
          why: { ar: "رقم الشيك والمبلغ متطابقان", en: "Cheque number and amount match" },
          handling: [{ kind: "match", ref: "000412", amount: "-640.000" }],
        },
      ],
    },
  ],

  intake: [
    {
      id: "in-01",
      scenario: "S1",
      state: "NEEDS_REVIEW",
      channel: "EMAIL",
      sender: "ap@petra-packaging.jo",
      fileName: "Petra_7731.pdf",
      receivedAt: "2026-10-09T10:42:00+03:00",
      readFrom: "DOCUMENT",
      supplierId: "petra",
      supplierRef: "7731",
      date: "2026-10-02",
      dueDate: "2026-10-16",
      currency: "JOD",
      lines: PETRA_7731_LINES,
      evidence: "EINVOICE_PRESENT",
      reasons: ["LOW_CONFIDENCE_FIELDS"],
      pages: [page(petra7731, "Petra Packaging bill 7731", "فاتورة بترا للتغليف 7731")],
      reads: PETRA_7731_READS,
    },
    {
      id: "in-02",
      scenario: "S2",
      state: "DUPLICATE_BLOCKED",
      channel: "UPLOAD",
      fileName: "Petra_7731 (1).pdf",
      receivedAt: "2026-10-09T11:40:00+03:00",
      readFrom: "DOCUMENT",
      supplierId: "petra",
      supplierRef: "7731",
      date: "2026-10-02",
      dueDate: "2026-10-16",
      currency: "JOD",
      lines: PETRA_7731_LINES,
      evidence: "EINVOICE_PRESENT",
      reasons: ["EXACT_DUPLICATE"],
      duplicateOf: "in-01",
      pages: [page(petra7731, "Petra Packaging bill 7731", "فاتورة بترا للتغليف 7731")],
      reads: PETRA_7731_READS,
    },
    {
      id: "in-03",
      scenario: "S3",
      state: "POSSIBLE_DUPLICATE",
      channel: "EMAIL",
      sender: "billing@orange-jordan.example",
      fileName: "Orange_64-5590.pdf",
      receivedAt: "2026-10-06T08:15:00+03:00",
      readFrom: "DOCUMENT",
      qr: "DECODED",
      supplierId: "orange",
      supplierRef: "64-5590",
      date: "2026-10-06",
      dueDate: "2026-10-20",
      currency: "JOD",
      lines: ORANGE_MONTHLY,
      evidence: "EINVOICE_PRESENT",
      reasons: ["POSSIBLE_DUPLICATE"],
      duplicateOf: "in-04",
      pages: [page(orange5590, "Orange Jordan bill 64-5590", "فاتورة أورنج الأردن 64-5590")],
      reads: docReads(LTR, {
        supplier: "ORANGE JORDAN",
        supplierRef: "64-5590",
        date: "2026-10-06",
        dueDate: "2026-10-20",
        lines: ["Mobile and internet, October 2026"],
        total: "64.960",
      }),
    },
    {
      id: "in-04",
      scenario: "S4",
      state: "READY",
      channel: "EMAIL",
      sender: "billing@orange-jordan.example",
      fileName: "Orange_64-5571.pdf",
      receivedAt: "2026-10-03T08:12:00+03:00",
      readFrom: "EINVOICE_QR",
      qr: "CROSS_CHECKED",
      supplierId: "orange",
      supplierRef: "64-5571",
      date: "2026-10-03",
      dueDate: "2026-10-17",
      currency: "JOD",
      lines: ORANGE_MONTHLY,
      evidence: "EINVOICE_PRESENT",
      reasons: ["READY"],
      pages: [page(orange5571, "Orange Jordan bill 64-5571", "فاتورة أورنج الأردن 64-5571")],
      reads: docReads(LTR, {
        supplier: "ORANGE JORDAN",
        supplierRef: "64-5571",
        date: "2026-10-03",
        dueDate: "2026-10-17",
        lines: ["Mobile and internet, October 2026"],
        total: "64.960",
      }),
    },
    {
      // S5 and S12: a receipt photo with no e-invoice, dated in the soft-closed September.
      id: "in-05",
      scenario: "S5",
      state: "NEEDS_REVIEW",
      channel: "UPLOAD",
      fileName: "IMG_4512.jpg",
      receivedAt: "2026-10-08T17:05:00+03:00",
      readFrom: "DOCUMENT",
      supplierId: "alsharq",
      supplierRef: "4512",
      date: "2026-09-28",
      currency: "JOD",
      lines: [
        line(
          ["Packing supplies", "مستلزمات تغليف"],
          "5150",
          "1",
          "75.000",
          "75.000",
          "S",
          "12.000",
        ),
      ],
      evidence: "EINVOICE_MISSING",
      reasons: ["NO_JOFOTARA_INVOICE", "PERIOD_LOCKED"],
      pages: [page(alsharq4512, "Al-Sharq Supplies receipt 4512", "إيصال الشرق للتوريدات 4512")],
      reads: docReads(
        RTL,
        {
          supplier: "الشرق للتوريدات",
          supplierRef: "4512",
          date: "2026-09-28",
          lines: ["مستلزمات تغليف"],
          total: "87.000",
        },
        {
          supplierRef: { raw: "٤٥١٢" },
          date: { raw: "٢٨/٠٩/٢٠٢٦" },
          total: { raw: "٨٧٫٠٠٠" },
        },
      ),
    },
    {
      id: "in-06",
      scenario: "S6",
      state: "READY",
      channel: "EMAIL",
      sender: "accounts@brightway-packaging.example",
      fileName: "SB-2410.pdf",
      receivedAt: "2026-10-02T06:30:00+03:00",
      readFrom: "DOCUMENT",
      supplierId: "shenzhen",
      supplierRef: "SB-2410",
      date: "2026-10-02",
      dueDate: "2026-10-31",
      currency: "USD",
      foreign: {
        currency: "USD",
        amount: "2400.00",
        rate: "0.709000",
        source: "CBJ",
        rateDate: "2026-10-02",
      },
      lines: [
        line(
          ["Printed mailer boxes", "علب شحن مطبوعة"],
          "5150",
          "1",
          "1701.600",
          "1701.600",
          "O",
          ZERO,
        ),
      ],
      evidence: "NOT_APPLICABLE",
      reasons: ["READY"],
      pages: [
        page(shenzhen2410, "Shenzhen Brightway invoice SB-2410", "فاتورة شنتشن برايتواي SB-2410"),
      ],
      reads: docReads(LTR, {
        supplier: "SHENZHEN BRIGHTWAY PACKAGING CO.",
        supplierRef: "SB-2410",
        date: "2026-10-02",
        dueDate: "2026-10-31",
        lines: ["Printed mailer boxes"],
        total: "2400.00",
      }),
    },
    {
      // S7: the IBAN printed on the document is not taken.
      id: "in-07",
      scenario: "S7",
      state: "NEEDS_REVIEW",
      channel: "UPLOAD",
      fileName: "AlAmal_5521.pdf",
      receivedAt: "2026-10-07T09:20:00+03:00",
      readFrom: "DOCUMENT",
      supplierId: "alamal",
      supplierRef: "5521",
      date: "2026-10-05",
      dueDate: "2026-10-19",
      currency: "JOD",
      lines: [
        line(
          ["Printed carton sleeves", "أغلفة كراتين مطبوعة"],
          "5150",
          "1",
          "300.000",
          "300.000",
          "S",
          "48.000",
        ),
      ],
      evidence: "EINVOICE_PRESENT",
      reasons: ["NEW_SUPPLIER"],
      pages: [page(alamal5521, "Al-Amal Printing bill 5521", "فاتورة مطبعة الأمل 5521")],
      reads: docReads(LTR, {
        supplier: "AL-AMAL PRINTING · مطبعة الأمل",
        supplierRef: "5521",
        date: "2026-10-05",
        dueDate: "2026-10-19",
        lines: ["Printed carton sleeves"],
        total: "348.000",
        iban: "JO88AABK1100000000110000331100",
      }),
    },
    {
      // S8: the bill's bank account (…7712) differs from the one on file (…4410).
      id: "in-08",
      scenario: "S8",
      state: "NEEDS_REVIEW",
      channel: "EMAIL",
      sender: "rent@alnoor-realestate.example",
      fileName: "AlNoor_Oct_rent.pdf",
      receivedAt: "2026-10-01T12:00:00+03:00",
      readFrom: "DOCUMENT",
      supplierId: "alnoor",
      supplierRef: "R-2026-10",
      date: "2026-10-01",
      dueDate: "2026-10-05",
      currency: "JOD",
      lines: [
        line(
          ["Warehouse rent, October 2026", "إيجار المستودع، تشرين الأول 2026"],
          "5120",
          "1",
          "850.000",
          "850.000",
          "O",
          ZERO,
        ),
      ],
      evidence: "NOT_APPLICABLE",
      reasons: ["IBAN_CHANGED"],
      pages: [
        page(
          alnoorOct,
          "Al-Noor Real Estate rent bill R-2026-10",
          "فاتورة إيجار النور العقارية R-2026-10",
        ),
      ],
      reads: docReads(
        RTL,
        {
          supplier: "النور العقارية",
          supplierRef: "R-2026-10",
          date: "2026-10-01",
          dueDate: "2026-10-05",
          lines: ["إيجار المستودع، تشرين الأول"],
          total: "850.000",
          iban: "JO44CAAB1050000000105000227712",
        },
        { date: { raw: "٠١/١٠/٢٠٢٦" }, dueDate: { raw: "٠٥/١٠/٢٠٢٦" } },
      ),
    },
    {
      // S9: the QR says 580.000, the AI read 508.000.
      id: "in-09",
      scenario: "S9",
      state: "NEEDS_REVIEW",
      channel: "EMAIL",
      sender: "office@aladwan-law.example",
      fileName: "ADW-1192.pdf",
      receivedAt: "2026-10-04T14:10:00+03:00",
      readFrom: "DOCUMENT",
      qr: "DECODED",
      supplierId: "aladwan",
      supplierRef: "ADW-1192",
      date: "2026-10-04",
      dueDate: "2026-10-18",
      currency: "JOD",
      lines: [
        line(
          ["Legal retainer, October 2026", "أتعاب قانونية شهرية، تشرين الأول 2026"],
          "5140",
          "1",
          "437.931",
          "437.931",
          "S",
          "70.069",
        ),
      ],
      qrLines: [
        line(
          ["Legal retainer, October 2026", "أتعاب قانونية شهرية، تشرين الأول 2026"],
          "5140",
          "1",
          "500.000",
          "500.000",
          "S",
          "80.000",
        ),
      ],
      evidence: "EINVOICE_PRESENT",
      reasons: ["QR_TOTAL_MISMATCH"],
      pages: [
        page(
          aladwan1192,
          "Al-Adwan Law Office bill ADW-1192",
          "فاتورة مكتب العدوان للمحاماة ADW-1192",
        ),
      ],
      reads: docReads(
        LTR,
        {
          supplier: "AL-ADWAN LAW OFFICE",
          supplierRef: "ADW-1192",
          date: "2026-10-04",
          dueDate: "2026-10-18",
          lines: ["Legal retainer, October 2026"],
          total: "508.000",
        },
        { total: { band: "CHECK", raw: "508.000" } },
      ),
    },
    {
      id: "in-10",
      scenario: "S10",
      state: "UNREADABLE",
      channel: "UPLOAD",
      fileName: "photo_2026-10-08.jpg",
      receivedAt: "2026-10-08T19:44:00+03:00",
      supplierId: null,
      currency: "JOD",
      evidence: "NOT_APPLICABLE",
      reasons: ["UNREADABLE"],
      pages: [page(blurryPhoto, "A blurred photo of a bill", "صورة غير واضحة لفاتورة")],
    },
    {
      id: "in-11",
      scenario: "S10",
      state: "QUARANTINED",
      channel: "EMAIL",
      sender: "invoices@petra-packagng.co",
      fileName: "Invoice_7740.pdf",
      receivedAt: "2026-10-09T08:05:00+03:00",
      supplierId: null,
      currency: "JOD",
      evidence: "NOT_APPLICABLE",
      reasons: [],
    },
    {
      id: "in-12",
      scenario: "S10",
      state: "PROCESSING",
      channel: "UPLOAD",
      fileName: "scan_0042.pdf",
      receivedAt: "2026-10-09T12:00:00+03:00",
      supplierId: null,
      currency: "JOD",
      evidence: "NOT_APPLICABLE",
      reasons: [],
    },
    {
      // S11: white-on-white text in the PDF tried to instruct the AI; it was ignored.
      id: "in-13",
      scenario: "S11",
      state: "NEEDS_REVIEW",
      channel: "EMAIL",
      sender: "sales@globaloffice.example",
      fileName: "GOS-5529.pdf",
      receivedAt: "2026-10-05T10:30:00+03:00",
      readFrom: "DOCUMENT",
      supplierId: "globaloffice",
      supplierRef: "GOS-5529",
      date: "2026-10-05",
      dueDate: "2026-10-19",
      currency: "JOD",
      lines: [
        line(["Shipping labels", "ملصقات شحن"], "5150", "1", "100.000", "100.000", "S", "16.000"),
      ],
      evidence: "EINVOICE_PRESENT",
      reasons: ["HIDDEN_INSTRUCTIONS"],
      pages: [
        page(
          globalOffice5529,
          "Global Office Supplies bill GOS-5529",
          "فاتورة التوريدات المكتبية العالمية GOS-5529",
        ),
      ],
      reads: docReads(LTR, {
        supplier: "GLOBAL OFFICE SUPPLIES",
        supplierRef: "GOS-5529",
        date: "2026-10-05",
        dueDate: "2026-10-19",
        lines: ["Shipping labels"],
        total: "116.000",
      }),
    },
    {
      id: "in-14",
      state: "READY",
      channel: "EMAIL",
      sender: "billing@alsharq-supplies.example",
      fileName: "AS-3402.pdf",
      receivedAt: "2026-10-08T09:00:00+03:00",
      readFrom: "EINVOICE_QR",
      qr: "CROSS_CHECKED",
      supplierId: "alsharq",
      supplierRef: "AS-3402",
      date: "2026-10-07",
      dueDate: "2026-10-21",
      currency: "JOD",
      lines: [
        line(["Strapping tape", "شريط تحزيم"], "5150", "40", "5.000", "200.000", "S", "32.000"),
      ],
      evidence: "EINVOICE_PRESENT",
      reasons: ["READY"],
      pages: [
        page(alsharq3402, "Al-Sharq Supplies bill AS-3402", "فاتورة الشرق للتوريدات AS-3402"),
      ],
      reads: docReads(LTR, {
        supplier: "AL-SHARQ SUPPLIES · الشرق للتوريدات",
        supplierRef: "AS-3402",
        date: "2026-10-07",
        dueDate: "2026-10-21",
        lines: ["Strapping tape"],
        total: "232.000",
      }),
    },
    // Posted from the inbox: each keeps its page, the bill's source document.
    ...(
      [
        ["in-15", "BL-2026-0081", globalOffice5480, "GLOBAL OFFICE SUPPLIES"],
        ["in-16", "BL-2026-0082", alsharq3310, "AL-SHARQ SUPPLIES · الشرق للتوريدات"],
        ["in-17", "BL-2026-0084", orange5512, "ORANGE JORDAN"],
        ["in-18", "BL-2026-0085", petra7715, "PETRA PACKAGING"],
        ["in-19", "BL-2026-0093", petra7728, "PETRA PACKAGING"],
      ] as const
    ).map(([id, billId, src, printedName]): IntakeItem => {
      const bill = NADA_BILLS.find((b) => b.id === billId) as Bill;
      return {
        id,
        state: "POSTED",
        channel: "EMAIL",
        fileName: `${bill.supplierRef}.pdf`,
        receivedAt: `${bill.date}T09:00:00+03:00`,
        readFrom: "DOCUMENT",
        supplierId: bill.supplierId,
        supplierRef: bill.supplierRef,
        date: bill.date,
        ...(bill.dueDate !== null ? { dueDate: bill.dueDate } : {}),
        currency: "JOD",
        lines: bill.lines,
        evidence: "EINVOICE_PRESENT",
        reasons: [],
        billId,
        pages: [
          page(src, `${printedName} ${bill.supplierRef}`, `${printedName} ${bill.supplierRef}`),
        ],
        reads: docReads(LTR, {
          supplier: printedName,
          supplierRef: bill.supplierRef,
          date: bill.date,
          ...(bill.dueDate !== null ? { dueDate: bill.dueDate } : {}),
          lines: bill.lines.map((l) => l.description.en),
          total: documentTotals(bill.lines).total,
        }),
      };
    }),
    {
      id: "in-20",
      state: "REJECTED",
      channel: "UPLOAD",
      fileName: "receipt_lunch.jpg",
      receivedAt: "2026-10-04T15:30:00+03:00",
      readFrom: "DOCUMENT",
      supplierId: null,
      supplierName: { ar: "مطعم القدس", en: "Al-Quds Restaurant" },
      date: "2026-10-03",
      currency: "JOD",
      lines: [line(["Lunch", "غداء"], "5150", "1", "20.259", "20.259", "S", "3.241")],
      evidence: "EINVOICE_MISSING",
      reasons: [],
      rejectReason: "NOT_OURS",
    },
    {
      // Samer asked Layla about it, so it waits on her.
      id: "in-21",
      state: "WAITING_ON_OTHERS",
      channel: "EMAIL",
      sender: "sales@globaloffice.example",
      fileName: "GOS-5541.pdf",
      receivedAt: "2026-10-06T13:20:00+03:00",
      readFrom: "DOCUMENT",
      supplierId: "globaloffice",
      supplierRef: "GOS-5541",
      date: "2026-10-06",
      dueDate: "2026-10-20",
      currency: "JOD",
      lines: [
        line(["Printer paper A4", "ورق طباعة A4"], "5150", "10", "5.000", "50.000", "S", "8.000"),
      ],
      evidence: "EINVOICE_PRESENT",
      reasons: ["READY"],
      note: {
        ar: "هل هذا الورق لفرع الزرقاء؟",
        en: "Is this paper for the Zarqa branch?",
      },
      pages: [
        page(
          globalOffice5541,
          "Global Office Supplies bill GOS-5541",
          "فاتورة التوريدات المكتبية العالمية GOS-5541",
        ),
      ],
      reads: docReads(LTR, {
        supplier: "GLOBAL OFFICE SUPPLIES",
        supplierRef: "GOS-5541",
        date: "2026-10-06",
        dueDate: "2026-10-20",
        lines: ["Printer paper A4"],
        total: "58.000",
      }),
    },
  ],

  workItems: [
    {
      id: "wi-01",
      reason: "READY",
      source: "AI",
      assignee: "layla",
      createdOn: "2026-10-08",
      dueOn: "2026-10-10",
      ref: "in-04",
      amount: "64.960",
    },
    {
      id: "wi-02",
      scenario: "S2",
      reason: "EXACT_DUPLICATE",
      source: "AI",
      assignee: "layla",
      createdOn: "2026-10-08",
      dueOn: "2026-10-10",
      ref: "in-02",
      amount: "549.840",
    },
    {
      id: "wi-03",
      scenario: "S8",
      reason: "IBAN_CHANGED",
      source: "AI",
      assignee: "layla",
      createdOn: "2026-10-08",
      dueOn: "2026-10-09",
      ref: "in-08",
      amount: "850.000",
    },
    {
      id: "wi-04",
      scenario: "S7",
      reason: "NEW_SUPPLIER",
      source: "AI",
      assignee: "layla",
      createdOn: "2026-10-07",
      dueOn: "2026-10-12",
      ref: "in-07",
      amount: "348.000",
    },
    {
      id: "wi-05",
      scenario: "S25",
      reason: "OWNER_EASY_ENTRY",
      source: "samer",
      assignee: "layla",
      createdOn: "2026-10-06",
      dueOn: "2026-10-09",
      ref: "EE-2026-0012",
      amount: "5000.000",
    },
    {
      id: "wi-06",
      scenario: "S24",
      reason: "BACKDATED_ENTRY",
      source: "AI",
      assignee: null,
      createdOn: "2026-10-07",
      dueOn: "2026-10-14",
      ref: "JE-2026-0431",
      amount: "310.000",
    },
    {
      id: "wi-07",
      scenario: "S26",
      reason: "QUESTION_FOR_OWNER",
      source: "layla",
      assignee: "samer",
      createdOn: "2026-10-03",
      dueOn: "2026-10-10",
      ref: "L10",
      amount: "1200.000",
      message: {
        ar: "ما هو الإيداع النقدي بقيمة 1,200.000 يوم 28 أيلول؟",
        en: "What is the 1,200.000 cash deposit on 28 Sep?",
      },
    },
    {
      id: "wi-08",
      scenario: "S21",
      reason: "EINVOICE_REJECTED",
      source: "AI",
      assignee: "layla",
      createdOn: "2026-10-08",
      dueOn: "2026-10-11",
      ref: "INV-2026-0121",
      amount: "928.000",
    },
    {
      id: "wi-09",
      scenario: "S22",
      reason: "EINVOICE_OUTCOME_UNKNOWN",
      source: "AI",
      assignee: "layla",
      createdOn: "2026-10-07",
      dueOn: "2026-10-09",
      ref: "INV-2026-0122",
      amount: "2320.000",
    },
    {
      id: "wi-10",
      scenario: "S15",
      reason: "PAYMENT_AWAITING_RELEASE",
      source: "layla",
      assignee: "samer",
      createdOn: "2026-10-08",
      dueOn: "2026-10-09",
      ref: "PAY-2026-0047",
      amount: "1478.000",
    },
    {
      id: "wi-11",
      reason: "UNRECONCILED_LINES",
      source: "AI",
      assignee: "layla",
      createdOn: "2026-10-01",
      dueOn: "2026-10-12",
      ref: "arab-bank",
    },
    {
      id: "wi-12",
      scenario: "S5",
      reason: "NO_JOFOTARA_INVOICE",
      source: "AI",
      assignee: "layla",
      createdOn: "2026-10-08",
      dueOn: "2026-10-15",
      ref: "in-05",
      amount: "12.000",
    },
    {
      // A second ready draft, so two can be approved as one batch.
      id: "wi-13",
      scenario: "S6",
      reason: "READY",
      source: "AI",
      assignee: "layla",
      createdOn: "2026-10-02",
      dueOn: "2026-10-10",
      ref: "in-06",
      amount: "1701.600",
    },
  ],

  // The books open on 31 July with the brought-forward entry, so July and before come from here.
  cashHistory: [
    { month: "2026-05", in: "4210.000", out: "3905.500" },
    { month: "2026-06", in: "3860.250", out: "4120.000" },
    { month: "2026-07", in: "2975.000", out: "3340.750" },
  ],
};

const EMPTY: Dataset = {
  bankAccounts: [],
  suppliers: [],
  customers: [],
  bills: [],
  payments: [],
  receipts: [],
  invoices: [],
  cheques: [],
  journals: [],
  easyEntries: [],
  statements: [],
  intake: [],
  workItems: [],
  cashHistory: [],
};

/** Al-Nada Distribution is the empty company: every screen's empty state, for free. */
export const DATASETS: Readonly<Record<CompanyId, Dataset>> = {
  "nada-trading": NADA_TRADING,
  "nada-distribution": EMPTY,
};

// ---------------------------------------------------------------------------
// Derived figures
// ---------------------------------------------------------------------------

/** Subtotal, tax and total of a document's lines. */
export function documentTotals(lines: readonly DocLine[]): {
  subtotal: Decimal;
  discount: Decimal;
  tax: Decimal;
  total: Decimal;
} {
  const subtotal = sumMinor(
    lines.map((l) => l.amount),
    SCALE,
  );
  const discount = sumMinor(
    lines.map((l) => l.discount ?? ZERO),
    SCALE,
  );
  const tax = sumMinor(
    lines.map((l) => l.tax),
    SCALE,
  );
  return {
    subtotal,
    discount,
    tax,
    total: addMinor(subMinor(subtotal, discount, SCALE), tax, SCALE),
  };
}

/** What stops one journal line posting: a group account, a missing contact, or no single amount. */
export type JournalLineProblem = "NOT_POSTABLE" | "CONTACT" | "AMOUNT";

export function journalLineProblem(line: JournalLine): JournalLineProblem | undefined {
  const positive = (amount: Decimal | undefined): boolean =>
    amount !== undefined && cmpMinor(amount, ZERO, SCALE) > 0;
  if (ACCOUNTS.find((a) => a.code === line.account)?.parent === undefined) return "NOT_POSTABLE";
  if (CONTROL_ACCOUNTS.includes(line.account) && (line.contactId ?? "") === "") return "CONTACT";
  return positive(line.debit) === positive(line.credit) ? "AMOUNT" : undefined;
}

/** Debits less credits: 0.000 when the entry balances. A balancing line is never added for you. */
export const journalDifference = (lines: readonly JournalLine[]): Decimal =>
  subMinor(
    sumMinor(
      lines.map((l) => l.debit ?? ZERO),
      SCALE,
    ),
    sumMinor(
      lines.map((l) => l.credit ?? ZERO),
      SCALE,
    ),
    SCALE,
  );

/** Can these lines post: two or more, each valid, debits equal to credits? */
export const journalPostable = (lines: readonly JournalLine[]): boolean =>
  lines.length >= 2 &&
  lines.every((l) => journalLineProblem(l) === undefined) &&
  cmpMinor(journalDifference(lines), ZERO, SCALE) === 0;

export interface Entry {
  id: string;
  date: string;
  lines: readonly JournalLine[];
}

const isPostedPayment = (p: Payment): boolean => p.state === "RELEASED" || p.state === "SETTLED";

export function billEntry(bill: Bill): Entry {
  const { tax, total } = documentTotals(bill.lines);
  // A credit note posts the bill's lines the other way round.
  const post = (account: string, amount: Decimal, cost: boolean): JournalLine =>
    cost === (bill.kind === "BILL") ? { account, debit: amount } : { account, credit: amount };
  return {
    id: bill.id,
    date: bill.accountingDate ?? bill.date,
    lines: [
      ...bill.lines.map((l) => ({
        ...post(l.account, l.amount, true),
        description: l.description,
      })),
      ...(cmpMinor(tax, ZERO, SCALE) === 0 ? [] : [post("1140", tax, true)]),
      { ...post("2101", total, false), contactId: bill.supplierId },
    ],
  };
}

/** What the allocations apply: bills positive, credits used negative. */
export const appliedOf = (payment: Payment): Decimal =>
  sumMinor(
    payment.allocations.map((a) => a.amount),
    SCALE,
  );

/** The money sent: the amount entered, or what is applied less the tax withheld. */
export const cashOf = (payment: Payment): Decimal =>
  payment.amount ?? subMinor(appliedOf(payment), payment.withholding?.amount ?? ZERO, SCALE);

/** Money sent beyond what is applied: an advance the supplier now owes back. */
export const advanceOf = (payment: Payment): Decimal =>
  subMinor(
    addMinor(cashOf(payment), payment.withholding?.amount ?? ZERO, SCALE),
    appliedOf(payment),
    SCALE,
  );

function paymentEntry(payment: Payment): Entry {
  const withheld = payment.withholding?.amount ?? ZERO;
  const writeOff = payment.short?.choice === "WRITE_OFF" ? payment.short : undefined;
  // The supplier's balance falls by the cash, the tax withheld for them and any written-off rest.
  const cleared = addMinor(
    addMinor(cashOf(payment), withheld, SCALE),
    writeOff?.amount ?? ZERO,
    SCALE,
  );
  return {
    id: payment.id,
    date: payment.date,
    lines: [
      { account: "2101", contactId: payment.supplierId, debit: cleared },
      { account: payment.from, credit: cashOf(payment) },
      ...(payment.withholding === undefined ? [] : [{ account: "2130", credit: withheld }]),
      ...(writeOff === undefined ? [] : [{ account: writeOff.account, credit: writeOff.amount }]),
    ],
  };
}

function receiptEntry(receipt: Receipt): Entry {
  const amount = sumMinor(
    receipt.allocations.map((a) => a.amount),
    SCALE,
  );
  return {
    id: receipt.id,
    date: receipt.date,
    lines: [
      { account: receipt.to, debit: amount },
      { account: "1120", contactId: receipt.customerId, credit: amount },
    ],
  };
}

export function invoiceEntry(invoice: Invoice): Entry {
  const { tax, total } = documentTotals(invoice.lines);
  // A credit note posts the invoice's lines the other way round.
  const post = (account: string, amount: Decimal, receivable: boolean): JournalLine =>
    receivable === (invoice.kind !== "CREDIT")
      ? { account, debit: amount }
      : { account, credit: amount };
  return {
    id: invoice.id,
    date: invoice.date,
    lines: [
      { ...post("1120", total, true), contactId: invoice.customerId },
      ...invoice.lines.map((l) => ({
        ...post(l.account, subMinor(l.amount, l.discount ?? ZERO, SCALE), false),
        description: l.description,
      })),
      post("2120", tax, false),
    ],
  };
}

/** Every posted entry: manual journals plus the system entries the posted documents make. */
export function ledger(data: Dataset): Entry[] {
  return [
    ...data.journals.filter((j) => j.state !== "DRAFT"),
    ...data.bills.filter((b) => b.state === "POSTED").map(billEntry),
    ...data.payments.filter(isPostedPayment).map(paymentEntry),
    ...data.receipts.map(receiptEntry),
    ...data.invoices.filter((i) => i.state === "POSTED").map(invoiceEntry),
  ];
}

/** An account's balance, debit positive, from entries dated within `from`–`to` (inclusive). */
export function accountBalance(
  entries: readonly Entry[],
  account: string,
  range: { from?: string; to?: string } = {},
): Decimal {
  const lines = entries
    .filter((e) => (range.from ?? "") <= e.date && e.date <= (range.to ?? "9999-12-31"))
    .flatMap((e) => e.lines)
    .filter((l) => l.account === account);
  return subMinor(
    sumMinor(
      lines.map((l) => l.debit ?? ZERO),
      SCALE,
    ),
    sumMinor(
      lines.map((l) => l.credit ?? ZERO),
      SCALE,
    ),
    SCALE,
  );
}

/**
 * What is still open on a posted bill, credit or invoice: its total less what
 * posted payments and receipts applied. A credit's open amount is positive.
 */
export function openAmount(data: Dataset, documentId: string): Decimal {
  const bill = data.bills.find((b) => b.id === documentId);
  const invoice = data.invoices.find((i) => i.id === documentId);
  const total = documentTotals(bill?.lines ?? invoice?.lines ?? []).total;
  const posted = data.payments.filter(isPostedPayment);
  const applied = sumMinor(
    [
      ...[...posted, ...data.receipts]
        .flatMap((record) => record.allocations)
        .filter((a) => a.documentId === documentId)
        .map((a) => a.amount),
      // A written-off rest closes the bill too.
      ...posted.flatMap((p) =>
        p.short?.choice === "WRITE_OFF" && p.short.documentId === documentId
          ? [p.short.amount]
          : [],
      ),
      // A posted credit note settles what it returns.
      ...data.invoices
        .filter((c) => c.kind === "CREDIT" && c.creditOf === documentId && c.state === "POSTED")
        .map((c) => documentTotals(c.lines).total),
    ],
    SCALE,
  );
  return bill?.kind === "CREDIT"
    ? addMinor(total, applied, SCALE)
    : subMinor(total, applied, SCALE);
}

/** The statement's closing less its opening and every handled line: 0.000 once all are handled. */
export function differenceOf(statement: Statement): Decimal {
  const handled = statement.lines.filter((l) => l.state === "MATCHED").map((l) => l.amount);
  return subMinor(statement.closing, sumMinor([statement.opening, ...handled], SCALE), SCALE);
}

/** Another live bill from the same supplier with the same supplier's number: a duplicate blocks. */
export const duplicateOf = (data: Dataset, bill: Bill): Bill | undefined =>
  bill.supplierRef === ""
    ? undefined
    : data.bills.find(
        (b) =>
          b.id !== bill.id &&
          b.kind === "BILL" &&
          b.supplierId === bill.supplierId &&
          b.supplierRef === bill.supplierRef &&
          b.state !== "VOID_BEFORE_POSTING",
      );

/** Does the payment apply more to a bill or credit than it still has open? */
export const overAllocated = (data: Dataset, payment: Payment): boolean =>
  payment.allocations.some((a) => {
    const applied =
      cmpMinor(a.amount, ZERO, SCALE) < 0 ? subMinor(ZERO, a.amount, SCALE) : a.amount;
    return cmpMinor(applied, openAmount(data, a.documentId), SCALE) > 0;
  });

/** Where a bill or credit stands with payments; overdue once its due date has passed. */
export function settlementOf(
  data: Dataset,
  bill: Pick<Bill, "id" | "lines"> & { dueDate: string | null },
): SettlementState {
  const open = openAmount(data, bill.id);
  if (cmpMinor(open, ZERO, SCALE) <= 0) return "SETTLED";
  if (bill.dueDate !== null && bill.dueDate < TODAY) return "OVERDUE";
  return cmpMinor(open, documentTotals(bill.lines).total, SCALE) < 0 ? "PARTLY_SETTLED" : "UNPAID";
}

export interface FigureRow {
  id: string;
  amount: Decimal;
}

/** A figure on the owner home, and the rows its drill-down lists. */
export interface Figure {
  figure: Decimal;
  rows: readonly FigureRow[];
}

const negate = (amount: Decimal): Decimal => subMinor(ZERO, amount, SCALE);
const isOpen = (row: FigureRow): boolean => cmpMinor(row.amount, ZERO, SCALE) !== 0;

/**
 * The owner home's money figures. The headline comes from the ledger where one
 * exists, the drill-down rows from the documents; the self-check proves they agree.
 */
export function ownerHome(data: Dataset): {
  cashAndBank: Figure;
  owedToYou: Figure;
  youOwe: Figure;
  chequesInHand: Figure;
  september: Figure;
  deductionAtRisk: Figure;
} {
  const entries = ledger(data);
  const balance = (account: string): Decimal => accountBalance(entries, account);
  const posted = data.bills.filter((b) => b.state === "POSTED");
  const september = { from: "2026-09-01", to: "2026-09-30" };
  const profitAndLoss = ACCOUNTS.filter((a) => a.parent === "4" || a.parent === "5");
  const inHand = data.cheques.filter(
    (c) => c.direction === "RECEIVED" && (c.state === "IN_SAFE" || c.state === "UNDER_COLLECTION"),
  );
  const atRisk = data.intake.filter(
    (i) => i.evidence === "EINVOICE_MISSING" && i.state !== "REJECTED",
  );
  return {
    cashAndBank: {
      figure: sumMinor(["1101", "1102", "1103"].map(balance), SCALE),
      rows: data.bankAccounts.map((b) => ({ id: b.id, amount: balance(b.account) })),
    },
    owedToYou: {
      figure: balance("1120"),
      rows: data.invoices
        .filter((i) => i.state === "POSTED" && i.kind !== "CREDIT")
        .map((i) => ({ id: i.id, amount: openAmount(data, i.id) }))
        .filter(isOpen),
    },
    youOwe: {
      figure: negate(balance("2101")),
      rows: [
        ...posted.map((b) => {
          const open = openAmount(data, b.id);
          return { id: b.id, amount: b.kind === "CREDIT" ? negate(open) : open };
        }),
        // Money sent ahead of bills is owed back to you.
        ...data.payments
          .filter(isPostedPayment)
          .map((p) => ({ id: p.id, amount: negate(advanceOf(p)) })),
      ].filter(isOpen),
    },
    chequesInHand: {
      figure: sumMinor(
        inHand.map((c) => c.amount),
        SCALE,
      ),
      rows: inHand.map((c) => ({ id: c.number, amount: c.amount })),
    },
    // What the business made in September: income less costs, in the owner's words.
    september: {
      figure: negate(
        sumMinor(
          profitAndLoss.map((a) => accountBalance(entries, a.code, september)),
          SCALE,
        ),
      ),
      rows: profitAndLoss
        .map((a) => ({ id: a.code, amount: negate(accountBalance(entries, a.code, september)) }))
        .filter(isOpen),
    },
    deductionAtRisk: {
      figure: sumMinor(
        atRisk.map((i) => documentTotals(i.lines ?? []).tax),
        SCALE,
      ),
      rows: atRisk.map((i) => ({ id: i.id, amount: documentTotals(i.lines ?? []).tax })),
    },
  };
}

const CASH_ACCOUNTS: readonly string[] = ["1101", "1102", "1103"];

/**
 * Money in and out of cash and bank, month by month. A month on the books
 * adds up the ledger one entry at a time by its net effect on cash, so a move
 * between your own accounts is neither; a month before them comes from
 * `cashHistory` (the brought-forward entry is dated in the last of those, so
 * it never counts as money in).
 */
export function cashFlow(data: Dataset, months: readonly string[]): CashMonth[] {
  const entries = ledger(data);
  return months.map((month) => {
    const before = data.cashHistory.find((h) => h.month === month);
    if (before !== undefined) return before;
    const nets = entries
      .filter((e) => e.date.startsWith(`${month}-`))
      .map((e) => {
        const cash = e.lines.filter((l) => CASH_ACCOUNTS.includes(l.account));
        return subMinor(
          sumMinor(
            cash.map((l) => l.debit ?? ZERO),
            SCALE,
          ),
          sumMinor(
            cash.map((l) => l.credit ?? ZERO),
            SCALE,
          ),
          SCALE,
        );
      });
    return {
      month,
      in: sumMinor(
        nets.filter((n) => cmpMinor(n, ZERO, SCALE) > 0),
        SCALE,
      ),
      out: sumMinor(nets.filter((n) => cmpMinor(n, ZERO, SCALE) < 0).map(negate), SCALE),
    };
  });
}

/** What JoFotara would reject, checked before an invoice is issued. */
export const INVOICE_PROBLEMS = [
  "CUSTOMER",
  "LINES",
  "HEADER_DISCOUNT",
  "BUYER_ID",
  "BUYER_NAME",
] as const;
export type InvoiceProblem = (typeof INVOICE_PROBLEMS)[number];
/** A cash sale above this needs the buyer's name and ID, as every credit sale does. */
export const CASH_BUYER_LIMIT = "10000.000";

export function invoiceProblems(invoice: Invoice): InvoiceProblem[] {
  const total = documentTotals(invoice.lines).total;
  const generic = invoice.customerId === "cash";
  // The buyer's name and ID are required on credit sales and on cash sales above the limit.
  const buyerNeeded = invoice.terms === "CREDIT" || cmpMinor(total, CASH_BUYER_LIMIT, SCALE) > 0;
  return INVOICE_PROBLEMS.filter((problem) => {
    switch (problem) {
      case "CUSTOMER":
        return invoice.customerId === "";
      case "LINES":
        return !invoice.lines.some((l) => cmpMinor(l.amount, ZERO, SCALE) > 0);
      case "HEADER_DISCOUNT":
        return cmpMinor(invoice.headerDiscount ?? ZERO, ZERO, SCALE) > 0;
      case "BUYER_ID":
        return buyerNeeded && (invoice.buyerId?.value ?? "") === "";
      case "BUYER_NAME":
        return buyerNeeded && generic && (invoice.buyerName ?? "") === "";
    }
  });
}

/** The accountant queue's types: families of typed reasons. */
export const QUEUE_FAMILIES = {
  ai_drafts: ["READY", "LOW_CONFIDENCE_FIELDS", "HIDDEN_INSTRUCTIONS", "UNREADABLE"],
  duplicates: ["EXACT_DUPLICATE", "POSSIBLE_DUPLICATE"],
  suppliers: ["NEW_SUPPLIER", "IBAN_CHANGED"],
  einvoices: [
    "NO_JOFOTARA_INVOICE",
    "QR_TOTAL_MISMATCH",
    "EINVOICE_REJECTED",
    "EINVOICE_OUTCOME_UNKNOWN",
  ],
  money: ["PAYMENT_AWAITING_RELEASE", "UNRECONCILED_LINES"],
  owner_entries: ["OWNER_EASY_ENTRY"],
  backdated: ["BACKDATED_ENTRY", "PERIOD_LOCKED"],
  waiting_on_owner: ["QUESTION_FOR_OWNER"],
  // Questions the owner has answered (found by their answer, not their reason).
  answered: [],
} as const satisfies Record<string, readonly ReasonCode[]>;
export type QueueFamily = keyof typeof QUEUE_FAMILIES;
export const QUEUE_FAMILY_IDS = Object.keys(QUEUE_FAMILIES) as QueueFamily[];

/** Has the record behind a work item already been dealt with? */
export function workItemDone(data: Dataset, item: WorkItem): boolean {
  const ref = item.ref;
  return (
    data.intake.some((i) => i.id === ref && (i.state === "POSTED" || i.state === "REJECTED")) ||
    data.easyEntries.some((e) => e.id === ref && e.state === "POSTED") ||
    data.invoices.some((i) => i.id === ref && fiscalOfficial(i.fiscal)) ||
    data.payments.some((p) => p.id === ref && (p.state === "RELEASED" || p.state === "SETTLED")) ||
    data.statements.some(
      (s) => s.bankAccountId === ref && s.lines.every((l) => l.state === "MATCHED"),
    )
  );
}

/** Inbox states that need the user's attention ("Needs you"). */
export const NEEDS_YOU: readonly IntakeState[] = [
  "NEEDS_REVIEW",
  "POSSIBLE_DUPLICATE",
  "DUPLICATE_BLOCKED",
  "UNREADABLE",
  "SCAN_FAILED",
  "QUARANTINED",
];

/** The inbox tabs say what the user must do; each lists the states it shows. */
export const INBOX_TABS = {
  needs_you: [...NEEDS_YOU, "PROCESSING"],
  ready: ["READY"],
  waiting: ["WAITING_ON_OTHERS"],
  posted: ["POSTED"],
  rejected: ["REJECTED"],
} as const satisfies Record<string, readonly IntakeState[]>;
export type InboxTab = keyof typeof INBOX_TABS;
export const INBOX_TAB_IDS = Object.keys(INBOX_TABS) as InboxTab[];

/** A supplier's or customer's name. */
export const contactName = (data: Dataset, id: string | null): Bilingual | undefined =>
  (data.suppliers.find((s) => s.id === id) ?? data.customers.find((c) => c.id === id))?.name;

/** An ISO calendar date moved by whole days. */
export function addDays(iso: string, days: number): string {
  const date = isoToDate(iso) ?? new Date();
  date.setDate(date.getDate() + days);
  return dateToIso(date);
}

/** The period a date falls in. */
export const periodOf = (date: string): Period | undefined =>
  PERIODS.find((period) => period.from <= date && date <= period.to);

/** The bill an approved inbox item becomes. */
export function billFromIntake(
  item: IntakeItem,
  id: string,
  changes: { dueDate?: string; accountingDate?: string; lines?: readonly DocLine[] } = {},
): Bill {
  const accountingDate = changes.accountingDate;
  return {
    id,
    kind: "BILL",
    supplierId: item.supplierId ?? "",
    supplierRef: item.supplierRef ?? "",
    date: item.date ?? TODAY,
    dueDate: changes.dueDate ?? item.dueDate ?? null,
    ...(accountingDate !== undefined ? { accountingDate } : {}),
    state: "POSTED",
    evidence: item.evidence,
    branch: "amman-main",
    lines: changes.lines ?? item.lines ?? [],
    ...(item.foreign !== undefined ? { foreign: item.foreign } : {}),
  };
}
